import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { X, ChevronDown, Compass, Building2, Gem, Hammer, Building, Leaf, Warehouse, DoorOpen, Trees, Crown, Key, Boxes, ChefHat, Bath, Grid3x3, Layers3, Home, Droplet, Zap, Archive, Mountain, Lightbulb, BookOpen, FileSignature, ClipboardCheck, Map, User as UserIcon, Settings as SettingsIcon, MessageCircle, LogOut, Mail, Bell, PaintBucket, Waves, Sun, Wind, PenTool, Palette, Ruler, HardHat, Calculator, Wallet, Scale, Shield, SquareStack, LayoutGrid, LayoutDashboard, Brush, Fence, Construction, FileCheck } from 'lucide-react';
import { siteConfig, buildingTypes } from '../../data/mock';
import { useContactModal } from '../../context/ContactModalContext';
import { useAuthModal } from '../../context/AuthModalContext';
import { useMarketingAuth } from '../../hooks/useMarketingAuth';
import { getSupabase } from '../../workspace/lib/supabaseClient';
import EmailForwardingModal from '../../workspace/components/EmailForwardingModal';
import MegaMenuIconPreloader from '../MegaMenuIconPreloader';
import { useUserState } from '../../lib/userState';
import { WORKSPACE_TABS } from '../../workspace/components/WorkspaceNav';
import { useWorkspaceNotifications } from '../../workspace/hooks/useWorkspaceNotifications';
import MiniMatcher from '../recommendations/MiniMatcher';
import StateContextChip from '../seo/StateContextChip';
import SAOnly from '../geo/SAOnly';
import MiniHnlMatcher from '../recommendations/MiniHnlMatcher';
import MiniHomeDesignsMatcher from '../recommendations/MiniHomeDesignsMatcher';
import { BUILDERS_MATCHER, SUPPLIERS_MATCHER, TRADES_MATCHER, PROS_MATCHER } from '../../data/megaMenuMatchers';
import { getRecentHbhEpisodes } from '../../data/hbhEpisodes';
import HbhAttribution from '../watch/HbhAttribution';
import SmartIcon from '../SmartIcon';
import { ease, dur } from '../../lib/motion';
import './megaMenu.css';
import PreferredSourceButton from '../PreferredSourceButton';
import ProductsMegaMenu from './ProductsMegaMenu';
import { PRODUCTS_DEPARTMENTS, PRODUCTS_BRANDS } from '../../data/productsCatalog';

// ============================================================
// Mega-menu nav configuration
// Each top-level item can either route directly (`href`) or open
// a dropdown panel with curated children (`columns`).
// ============================================================
// Featured local estate tiles for the "Explore Home & Land" mega menu.
// Gradient tiles (no photos) keep the menu fast and on-brand. The full
// set lives on the estate index / directory pages. SA default below;
// VIC visitors get the Melbourne set via navForState.
const SA_ESTATE_TILES = [
  { name: 'Riverlea', locality: 'Buckland Park', href: '/land-estate/riverlea', accent: 'from-[#1d8efe] to-[#1670cc]', img: 'https://images.unsplash.com/photo-1617126891624-faf3af1106f7?w=500&q=70&auto=format&fit=crop', logo: 'https://www.google.com/s2/favicons?sz=128&domain=riverlea.com.au' },
  { name: 'Miravale', locality: 'Angle Vale', href: '/land-estate/miravale', accent: 'from-[#4abff2] to-[#1d8efe]', img: 'https://images.unsplash.com/photo-1772325482422-55bc8dd65662?w=500&q=70&auto=format&fit=crop', logo: 'https://www.google.com/s2/favicons?sz=128&domain=miravale.com.au' },
  { name: 'Liberty', locality: 'Two Wells', href: '/land-estate/liberty-two-wells', accent: 'from-[#1d8efe] to-[#033880]', img: 'https://images.pexels.com/photos/14650436/pexels-photo-14650436.jpeg?auto=compress&cs=tinysrgb&w=500', logo: 'https://www.google.com/s2/favicons?sz=128&domain=hickinbotham.com.au' },
  { name: 'Aston Hills', locality: 'Mount Barker', href: '/land-estate/aston-hills', accent: 'from-[#0ea5e9] to-[#1d8efe]', img: 'https://images.unsplash.com/photo-1599329973161-a41253ab4049?w=500&q=70&auto=format&fit=crop', logo: 'https://www.google.com/s2/favicons?sz=128&domain=astonhills.com.au' },
  { name: 'Aldinga', locality: 'Aldinga Beach', href: '/land-estate/aldinga', accent: 'from-[#1670cc] to-[#1d8efe]', img: 'https://images.pexels.com/photos/33569241/pexels-photo-33569241.jpeg?auto=compress&cs=tinysrgb&w=500', logo: 'https://www.google.com/s2/favicons?sz=128&domain=villawood.com.au' },
  { name: 'Burgundy Estate', locality: 'Angle Vale', href: '/land-estate/burgundy-estate', accent: 'from-[#4abff2] to-[#1670cc]', img: 'https://images.unsplash.com/photo-1600645174997-bea67efe2fd1?w=500&q=70&auto=format&fit=crop' },
];

const navConfig = [
  {
    name: "Builders",
    href: "/builders",
    // Hero buttons (top of menu) open MiniMatcher with a preset
    // category for the 3 highest-intent builder verticals. The
    // bottomMatcher (bottom right of menu) is the catch-all "I want
    // to shortlist a builder but my category isn't here" path - same
    // pattern as suppliers / trades / pros.
    bottomMatcher: {
      label: "Shortlist a builder",
      ...BUILDERS_MATCHER,
    },
    // Hero buttons open the OTP mini matcher (no account wall) via the
    // existing `/matcher` deep-link route, pre-selecting the builder
    // vertical. 2026-11-03: user requested these route through the mini
    // matcher instead of the account-gated Find-a-Builder workspace tool.
    heroButtons: [
      {
        title: "New home builder",
        subtitle: "Building from scratch - display home, custom or off-the-plan",
        href: "/matcher?category=professional:builder&heading=Find%20your%20new%20home%20builder&source=hero-new-home",
        accent: "from-[#1d8efe] to-[#1670cc]",
        eyebrow: "Find the right",
        eyebrowColor: "#febd59",
      },
      {
        title: "Renovation builder",
        subtitle: "Knock it down and rebuild, extend out, or remodel inside",
        href: "/matcher?category=professional:builder&heading=Find%20your%20renovation%20builder&source=hero-renovation",
        accent: "from-[#4abff2] to-[#1d8efe]",
        eyebrow: "Find the right",
        eyebrowColor: "#febd59",
      },
      {
        title: "Home improvement builder",
        subtitle: "Backyard projects - pergolas, decks, granny flats, sheds, pools",
        href: "/matcher?category=professional:home_improvement_builder&heading=Find%20your%20home%20improvement%20builder&source=hero-home-improvement",
        accent: "from-[#1d8efe] to-[#033880]",
        eyebrow: "Find the right",
        eyebrowColor: "#febd59",
      },
    ],
    circleStrip: [
      { name: "Custom",     href: "/learn/build-type/custom",        LucideIcon: Compass,   iconSrc: "/images/toolbox-icons/builder-types/custom.png" },
      { name: "Volume",     href: "/learn/build-type/volume",        LucideIcon: Building2, iconSrc: "/images/toolbox-icons/builder-types/volume.png" },
      { name: "Luxury",     href: "/learn/build-type/luxury",        LucideIcon: Crown,     iconSrc: "/images/toolbox-icons/builder-types/luxury.png" },
      { name: "Budget",     href: "/learn/build-type/budget",        LucideIcon: Gem,       iconSrc: "/images/toolbox-icons/builder-types/budget.png" },
      { name: "Knockdown",  href: "/learn/build-type/knockdown",     LucideIcon: Hammer,    iconSrc: "/images/toolbox-icons/builder-types/knockdown.png" },
      { name: "Two-storey", href: "/learn/build-type/two-storey",    LucideIcon: Building,  iconSrc: "/images/toolbox-icons/builder-types/two-storey.png" },
      { name: "Townhouse",  href: "/learn/build-type/townhouse",     LucideIcon: Warehouse, iconSrc: "/images/toolbox-icons/builder-types/townhouse.png" },
      { name: "Granny flat", href: "/learn/build-type/granny-flat",  LucideIcon: DoorOpen,  iconSrc: "/images/toolbox-icons/builder-types/granny-flat.png" },
      { name: "Country",    href: "/learn/build-type/country",     LucideIcon: Trees,     iconSrc: "/images/toolbox-icons/builder-types/country.png" },
      { name: "Acreage",    href: "/learn/build-type/acreage",     LucideIcon: Leaf,      iconSrc: "/images/toolbox-icons/builder-types/acreage.png" },
      { name: "First home", href: "/learn/build-type/first-home",  LucideIcon: Key,       iconSrc: "/images/toolbox-icons/builder-types/first-home.png" },
      { name: "Compare",    href: "/learn/build-type/compare",     LucideIcon: Boxes,     iconSrc: "/images/toolbox-icons/builder-types/compare.png" },
    ],
    columns: [
      {
        title: "Find a builder",
        links: [
          { name: "Best home builders Adelaide", href: "/home-builders-adelaide" },
          { name: "Compare Adelaide builders", href: "/compare" },
          { name: "Builders near me", href: "/home-builders-near-me-adelaide" },
          { name: "Our partner builders", href: "/building-partners" },
          { name: "Avoiding dodgy builders", href: "/dodgy-builders-adelaide" },
        ],
      },
      {
        title: "By builder type",
        links: [
          { name: "Custom home builders", href: "/custom-home-builders-adelaide" },
          { name: "Volume vs project", href: "/volume-builder-vs-project-builder-adelaide" },
          { name: "Luxury home builders", href: "/luxury-home-builders-adelaide" },
          { name: "Budget home builders", href: "/budget-home-builders-adelaide" },
          { name: "Top 5 Adelaide builders", href: "/top-5-home-builders-adelaide" },
        ],
      },
      {
        title: "Builder issues",
        links: [
          { name: "Changing builders", href: "/changing-builders" },
          { name: "Builder went bankrupt", href: "/builder-went-bankrupt-what-now" },
          { name: "Disputes with builders", href: "/dispute-with-builder-adelaide" },
          { name: "Common builder problems", href: "/common-builder-problems" },
          { name: "Questions to ask builders", href: "/questions-to-ask-builders" },
        ],
      },
    ],
  },
  {
    name: "Suppliers",
    href: "/suppliers",
    // Single bottom CTA opens the MiniMatcher which first asks the
    // user which kind of supplier they need (kitchens, bathrooms,
    // flooring, etc.) before the questions step kicks in.
    bottomMatcher: {
      label: "Shortlist a supplier",
      ...SUPPLIERS_MATCHER,
    },
    circleStrip: [
      { name: "Kitchens",        href: "/learn/kitchens",      LucideIcon: ChefHat,  iconSrc: "/images/toolbox-icons/matcher/kitchens.png" },
      { name: "Bathrooms",       href: "/learn/bathrooms",     LucideIcon: Bath,     iconSrc: "/images/toolbox-icons/matcher/bathrooms.png" },
      { name: "Tiles",           href: "/learn/tiles",         LucideIcon: Grid3x3,  iconSrc: "/images/toolbox-icons/matcher/tiles.png" },
      { name: "Flooring",        href: "/learn/flooring",      LucideIcon: Layers3,  iconSrc: "/images/toolbox-icons/matcher/flooring.png" },
      { name: "Stone",           href: "/learn/stone",         LucideIcon: Mountain, iconSrc: "/images/toolbox-icons/matcher/stone-benchtops.png" },
      { name: "Windows & doors", href: "/learn/windows-doors", LucideIcon: DoorOpen, iconSrc: "/images/toolbox-icons/matcher/windows-doors.png" },
      { name: "Roofing",         href: "/learn/roofing",       LucideIcon: Home,     iconSrc: "/images/toolbox-icons/matcher/roofing.png" },
      { name: "Plumbing",        href: "/learn/plumbing",      LucideIcon: Droplet,  iconSrc: "/images/toolbox-icons/matcher/plumber.png" },
      { name: "Electrical",      href: "/learn/electrical",    LucideIcon: Zap,      iconSrc: "/images/toolbox-icons/matcher/electrician.png" },
      { name: "Cabinetry",       href: "/learn/cabinetry",     LucideIcon: Archive,  iconSrc: "/images/toolbox-icons/matcher/carpenter.png" },
      { name: "Lighting",        href: "/learn/lighting",      LucideIcon: Lightbulb, iconSrc: "/images/toolbox-icons/matcher/lighting.png" },
      { name: "Landscaping",     href: "/learn/landscaping",   LucideIcon: Trees,    iconSrc: "/images/toolbox-icons/matcher/landscaper.png" },
    ],
    columns: [
      {
        title: "By category",
        links: [
          { name: "Concrete & footings", href: "/suppliers/category/concrete-and-footings" },
          { name: "Stone & surfaces", href: "/suppliers/category/stone-and-surfaces" },
          { name: "Landscaping", href: "/suppliers/category/landscaping" },
          { name: "Windows & doors", href: "/suppliers/category/windows-and-doors" },
          { name: "Flooring", href: "/suppliers/category/flooring" },
          { name: "Kitchens & appliances", href: "/suppliers/category/kitchens-and-appliances" },
        ],
      },
      {
        title: "Resources & guides",
        links: [
          { name: "Sustainable building products", href: "/suppliers/guides/sustainable-building-products" },
          { name: "Compare materials", href: "/suppliers/guides/compare-materials" },
          { name: "Questions to ask suppliers", href: "/suppliers/guides/questions-to-ask-suppliers" },
          { name: "Product specification guides", href: "/suppliers/guides/product-specification" },
          { name: "Innovative building products", href: "/suppliers/guides/innovative-building-products" },
        ],
      },
      {
        title: "Featured suppliers",
        links: [
          { name: "Sustainable Concrete Group", href: "/suppliers/sustainable-concrete-group" },
          { name: "Lamina Stone", href: "/suppliers/lamina-stone" },
          { name: "Best Solar and Batteries", href: "/suppliers/best-solar-and-batteries" },
          { name: "SolarQuotes", href: "/suppliers/solarquotes" },
          { name: "PayLocker", href: "/suppliers/paylocker" },
        ],
      },
    ],
  },
  {
    name: "Trades",
    href: "/find/category/tradies",
    bottomMatcher: {
      label: "Shortlist a tradie",
      ...TRADES_MATCHER,
    },
    circleStrip: [
      { name: "Concreter",    href: "/find/concreter",              LucideIcon: Layers3,     iconSrc: "/images/toolbox-icons/matcher/concreter.png" },
      { name: "Bricklayer",   href: "/find/bricklayer",             LucideIcon: SquareStack, iconSrc: "/images/toolbox-icons/matcher/bricklayer.png" },
      { name: "Carpenter",    href: "/find/carpenter",              LucideIcon: Hammer,      iconSrc: "/images/toolbox-icons/matcher/carpenter.png" },
      { name: "Roofer",       href: "/find/roofer",                 LucideIcon: Home,        iconSrc: "/images/toolbox-icons/matcher/roofer.png" },
      { name: "Electrician",  href: "/find/electrician",            LucideIcon: Zap,         iconSrc: "/images/toolbox-icons/matcher/electrician.png" },
      { name: "Plumber",      href: "/find/plumber",                LucideIcon: Droplet,     iconSrc: "/images/toolbox-icons/matcher/plumber.png" },
      { name: "Tiler",        href: "/find/tiler",                  LucideIcon: LayoutGrid,  iconSrc: "/images/toolbox-icons/matcher/tiler.png" },
      { name: "Painter",      href: "/find/painter",                LucideIcon: PaintBucket, iconSrc: "/images/toolbox-icons/matcher/painter.png" },
      { name: "Air con",      href: "/find/air-conditioning",       LucideIcon: Wind,        iconSrc: "/images/toolbox-icons/matcher/air-conditioning.png" },
      { name: "Solar",        href: "/find/solar-installer",        LucideIcon: Sun,         iconSrc: "/images/toolbox-icons/matcher/solar-batteries.png" },
      { name: "Landscaper",   href: "/find/landscaping-contractor", LucideIcon: Trees,       iconSrc: "/images/toolbox-icons/matcher/landscaper.png" },
      { name: "Pool builder", href: "/find/pool-builder",           LucideIcon: Waves,       iconSrc: "/images/toolbox-icons/matcher/pool-builder.png" },
    ],
    columns: [
      {
        title: "Shortlist a tradie",
        links: [
          { name: "Browse all trades", href: "/find/category/tradies", description: "A to Z of every trade we shortlist for." },
          { name: "Our partner trade network", href: "/building-partners", description: "Listed businesses we work with - check anyone's licence yourselfelf with our checker." },
          { name: "Talk to a real human", href: "/one-on-one-support", description: "Talk to a real person about the right trade." },
        ],
      },
      {
        title: "Hiring trades in SA",
        links: [
          { name: "Hiring trades, walk me through it", href: "/hiring-trades-guide", description: "Stage by stage CoPilot guide." },
          { name: "Just planning", href: "/hiring-trades-guide?stage=planning", description: "Things people check before any trade starts." },
          { name: "Frame and roof stage", href: "/hiring-trades-guide?stage=frame-roof", description: "Licences to check, inspections, and stage payments." },
          { name: "Fit-off and handover", href: "/hiring-trades-guide?stage=fit-off", description: "The finish-up before you get the keys, plus your snag list." },
        ],
      },
      {
        title: "When something is off",
        links: [
          { name: "Looking into a defect", href: "/hiring-trades-guide?stage=defect", description: "What the process usually looks like." },
          { name: "How we match you", href: "/legal/matching-policy", description: "Our matching standards and rules." },
          { name: "Reviews and feedback", href: "/legal/reviews-policy", description: "How we collect and moderate reviews." },
          { name: "Report a concern", href: "/legal/complaints-process", description: "Lodge a complaint about any partner trade." },
        ],
      },
    ],
  },
  {
    name: "Professionals",
    href: "/find/category/professional-services",
    bottomMatcher: {
      label: "Shortlist a professional",
      ...PROS_MATCHER,
    },
    circleStrip: [
      { name: "Architect",   href: "/find/architect",           LucideIcon: Compass,        iconSrc: "/images/toolbox-icons/matcher/architect.png" },
      { name: "Building Designer", href: "/find/building-designer", LucideIcon: PenTool,      iconSrc: "/images/toolbox-icons/matcher/building-designer.png" },
      { name: "Interior",    href: "/find/interior-designer",   LucideIcon: Palette,        iconSrc: "/images/toolbox-icons/matcher/interior-designer.png" },
      { name: "Town planner", href: "/find/town-planner",       LucideIcon: Map,            iconSrc: "/images/toolbox-icons/matcher/town-planner.png" },
      { name: "Surveyor",    href: "/find/surveyor",            LucideIcon: Ruler,          iconSrc: "/images/toolbox-icons/matcher/surveyor.png" },
      { name: "Energy",      href: "/find/energy-assessor",     LucideIcon: Leaf,           iconSrc: "/images/toolbox-icons/matcher/energy-assessor.png" },
      { name: "Structural",  href: "/find/structural-engineer", LucideIcon: HardHat,        iconSrc: "/images/toolbox-icons/matcher/structural-engineer.png" },
      { name: "QS",          href: "/find/quantity-surveyor",   LucideIcon: Calculator,     iconSrc: "/images/toolbox-icons/matcher/quantity-surveyor.png" },
      { name: "Inspector",   href: "/find/building-inspector",  LucideIcon: ClipboardCheck, iconSrc: "/images/toolbox-icons/matcher/building-inspector.png" },
      { name: "Finance",     href: "/find/finance-broker",      LucideIcon: Wallet,         iconSrc: "/images/toolbox-icons/matcher/finance-broker.png" },
      { name: "Conveyancer", href: "/find/conveyancer",         LucideIcon: FileSignature,  iconSrc: "/images/toolbox-icons/matcher/conveyancer.png" },
      { name: "Lawyer",      href: "/find/property-lawyer",     LucideIcon: Scale,          iconSrc: "/images/toolbox-icons/matcher/property-lawyer.png" },
      { name: "Insurance",   href: "/find/insurance-broker",    LucideIcon: Shield,         iconSrc: "/images/toolbox-icons/matcher/insurance-broker.png" },
      { name: "Certifier",   href: "/find/private-certifier",   LucideIcon: FileCheck,      iconSrc: "/images/toolbox-icons/matcher/private-certifier.png" },
    ],
    columns: [
      {
        title: "Shortlist a professional",
        links: [
          { name: "Browse all professionals", href: "/find/category/professional-services", description: "A to Z of every pro we shortlist for." },
          { name: "Our partner network", href: "/building-partners", description: "Listed architects, engineers, finance and legal businesses we work with." },
          { name: "Talk to a real human", href: "/one-on-one-support", description: "Talk to a real person about who to bring in." },
        ],
      },
      {
        title: "Hiring professionals",
        links: [
          { name: "Hiring pros, walk me through it", href: "/hiring-professionals-guide", description: "Decision by decision CoPilot guide." },
          { name: "Designing my home", href: "/hiring-professionals-guide?decision=designing", description: "Architect, building designer or draftie - who you need." },
          { name: "Council approval", href: "/hiring-professionals-guide?decision=approval", description: "Getting the green tick - town planner, surveyor, engineer." },
          { name: "Reviewing my contract", href: "/hiring-professionals-guide?decision=contract", description: "Have a pro check the fine print before you sign." },
          { name: "Sorting finance", href: "/hiring-professionals-guide?decision=finance", description: "Brokers, banks, grants and money-saver concessions." },
        ],
      },
      {
        title: "Inspections and after handover",
        links: [
          { name: "Build inspections", href: "/hiring-professionals-guide?decision=inspection", description: "Building inspector or engineer - extra eyes before you sign off." },
          { name: "After the build is done", href: "/hiring-professionals-guide?decision=post-build", description: "Worries about defects, plus tax reports if you’re investing." },
          { name: "Reviews and feedback", href: "/legal/reviews-policy", description: "How we collect and moderate reviews." },
          { name: "Report a concern", href: "/legal/complaints-process", description: "Lodge a complaint about any partner." },
        ],
      },
    ],
  },
  {
    name: "Products",
    href: "/products",
    productsMega: true,
  },
  {
    name: "Inspiration",
    href: "/inspiration",
    // Bottom CTA on the menu footer launches MiniHomeDesignsMatcher,
    // a streamlined 3-question version of the floor-plan library
    // (stories, bedrooms, size) scoring against /api/home-designs/plans.
    bottomHomeDesignsMatcher: {
      label: "Shortlist a floor plan",
    },
    heroButtons: [
      {
        title: "Inspiration Hub",
        subtitle: "Dream it here, build it with the right people",
        href: "/inspiration",
        image: "/inspiration/inspiration-hub.jpg",
        alt: "Contemporary Australian family home glowing at dusk",
        accent: "from-[#001133] to-[#1d8efe]",
        eyebrow: "Start here",
        eyebrowColor: "#febd59",
      },
      {
        title: "Home Designs",
        subtitle: "Browse 760+ designs · 10 partner builders",
        href: "/home-designs",
        image: "/inspiration/home-designs.jpg",
        alt: "Modern single-storey Australian home facade in daylight",
        accent: "from-[#1d8efe] to-[#1670cc]",
        eyebrow: "760+ plans",
        eyebrowColor: "#febd59",
      },
      {
        title: "Display Homes",
        subtitle: "Walk through homes across Adelaide & Melbourne",
        href: "/display-homes",
        image: "/inspiration/display-homes.jpg",
        alt: "Bright open-plan display home kitchen and living area",
        accent: "from-[#4abff2] to-[#1d8efe]",
        eyebrow: "Visit in person",
        eyebrowColor: "#febd59",
      },
      {
        title: "Design by Build Type",
        subtitle: "New homes, renos, pools, decks and more",
        href: "/build-types",
        image: "/inspiration/build-types.jpg",
        alt: "Australian alfresco deck with pergola and outdoor lounge",
        accent: "from-[#1d8efe] to-[#033880]",
        eyebrow: "10 build types",
        eyebrowColor: "#febd59",
      },
    ],
    chipStrip: [
      { name: "Victorian", href: "/home-styles/victorian", image: "/images/home-styles/victorian/hero.jpeg" },
      { name: "Edwardian", href: "/home-styles/edwardian-federation", image: "/images/home-styles/edwardian-federation/hero.jpeg" },
      { name: "Interwar & Deco", href: "/home-styles/interwar-deco", image: "/images/home-styles/interwar-deco/hero.jpeg" },
      { name: "Mid-century", href: "/home-styles/mid-century-modern", image: "/images/home-styles/mid-century-modern/hero.jpeg" },
      { name: "Hamptons", href: "/home-styles/hamptons", image: "/images/home-styles/hamptons/hero.jpeg" },
      { name: "Contemporary", href: "/home-styles/contemporary", image: "/images/home-styles/contemporary/hero.jpeg" },
      { name: "Coastal", href: "/home-styles/coastal", image: "/images/home-styles/coastal/hero.jpeg" },
      { name: "Barn & farmhouse", href: "/home-styles/barn-farmhouse", image: "/images/home-styles/barn-farmhouse/hero.jpeg" },
    ],
    columns: [
      {
        title: "Home styles (interiors)",
        links: [
          { name: "Hamptons & coastal", href: "/home-styles/hamptons" },
          { name: "Contemporary modern", href: "/home-styles/contemporary" },
          { name: "Victorian & Federation", href: "/home-styles/victorian" },
          { name: "Mid-century modern", href: "/home-styles/mid-century-modern" },
          { name: "All home styles & eras", href: "/home-styles" },
        ],
      },
      {
        title: "Home designs & display",
        links: [
          { name: "All home designs", href: "/home-designs" },
          { name: "Display homes", href: "/display-homes" },
          { name: "Single vs double storey", href: "/single-vs-double-storey" },
          { name: "Display home guide", href: "/display-home-guide" },
          { name: "Display home checklist", href: "/display-home-checklist" },
        ],
      },
      {
        title: "Design by build type",
        links: [
          { name: "New homes", href: "/build-type-design/new-homes" },
          { name: "Renovations & extensions", href: "/build-type-design/renovations" },
          { name: "Granny flats", href: "/build-type-design/granny-flats" },
          { name: "Pools & outdoor living", href: "/build-type-design/pools-and-outdoor" },
          { name: "All build types", href: "/build-types" },
        ],
      },
    ],
  },
  {
    name: "Explore Home & Land",
    href: "/house-and-land-adelaide",
    // The catch-all CTA on the right of the menu footer opens the
    // MiniHnlMatcher (a streamlined 3-question version of the
    // workspace's full House & Land Matchmaker, scoped to popup UX).
    bottomHnlMatcher: {
      label: "Shortlist home & land available now",
    },
    heroButtons: [
      {
        title: "Home & Land Packages",
        subtitle: "Live BuildPilot inventory · Adelaide",
        href: "/house-and-land-adelaide",
        accent: "from-[#1d8efe] to-[#1670cc]",
        eyebrow: "Live now",
        eyebrowColor: "#febd59",
      },
      {
        title: "Land for Sale",
        subtitle: "Vacant blocks from selling agents",
        href: "/land-for-sale-adelaide",
        accent: "from-[#4abff2] to-[#1d8efe]",
        eyebrow: "Live now",
        eyebrowColor: "#febd59",
      },
      {
        title: "Repayment Calculator",
        subtitle: "Powered by Equable · Indicative weekly / monthly figures",
        href: "/p/UAklUjJ9rbMGQITCzutU5tSrPtMF_ky9_2L4aP4TScs?token=eq_bp_2026_UAklUjJ9rbMGQITCzutU5tSrPtMF",
        accent: "from-[#1d8efe] to-[#033880]",
        eyebrow: "Powered by Equable",
        eyebrowColor: "#febd59",
      },
    ],
    columns: [
      {
        title: "Browse suburbs",
        links: [
          { name: "All Adelaide suburbs", href: "/south-australia/adelaide/areas" },
          { name: "Northern Adelaide", href: "/home-builders-northern-adelaide" },
          { name: "Southern Adelaide", href: "/home-builders-southern-adelaide" },
          { name: "Adelaide Hills", href: "/home-builders-adelaide-hills" },
          { name: "Western Adelaide", href: "/home-builders-western-adelaide" },
        ],
      },
      {
        title: "By council & land",
        links: [
          { name: "Onkaparinga Council", href: "/building-in-onkaparinga-council" },
          { name: "Playford Council", href: "/building-in-playford-council" },
          { name: "Salisbury Council", href: "/building-in-salisbury-council" },
          { name: "Tea Tree Gully", href: "/building-in-tea-tree-gully-council" },
          { name: "Charles Sturt Council", href: "/building-in-charles-sturt-council" },
          { name: "Land estates Adelaide", href: "/land-estate" },
          { name: "Choosing land", href: "/choosing-land" },
        ],
      },
      {
        title: "Live Home & Land packages",
        links: [
          { name: "All live packages →", href: "/house-and-land-adelaide" },
          { name: "Land for sale", href: "/land-for-sale-adelaide" },
          { name: "3 bedroom homes", href: "/house-and-land-adelaide" },
          { name: "4 bedroom homes", href: "/house-and-land-adelaide" },
          { name: "5 bedroom homes", href: "/house-and-land-adelaide" },
          { name: "Single storey", href: "/house-and-land-adelaide" },
          { name: "Double storey", href: "/house-and-land-adelaide" },
          { name: "Cheapest land in SA", href: "/help/cheapest-land-south-australia" },
        ],
      },
    ],
    estateTiles: SA_ESTATE_TILES,
    estateTilesViewAll: { href: "/land-estate", label: "View all Adelaide estates" },
  },
  {
    name: "Read & Watch Guides",
    href: "/guides",
    watchStrip: {
      // Latest HBH podcast episodes as micro-cards between hero tiles and
      // the column links. 6 items in a 2 x 3 grid so users get a proper
      // taste of the library without leaving the mega menu.
      eyebrow: "Latest episodes",
      count: 6,
      ctaHref: "/watch",
      ctaLabel: "Watch all 134",
    },
    heroButtons: [
      {
        title: "Builder Selection Checklist",
        subtitle: "PDF · 18-point checklist",
        href: "/builder-selection-checklist",
        accent: "from-[#1d8efe] to-[#1670cc]",
        eyebrow: "Just starting",
        eyebrowColor: "#febd59",
        LucideIcon: BookOpen,
      },
      {
        title: "Contracts & Variations",
        subtitle: "What to check before you sign",
        href: "/building-a-new-home/contracts",
        accent: "from-[#4abff2] to-[#1d8efe]",
        eyebrow: "About to sign",
        eyebrowColor: "#febd59",
        LucideIcon: FileSignature,
      },
      {
        title: "Handover & Defects",
        subtitle: "Final walk-through inspection guide",
        href: "/building-a-new-home/build-timeline",
        accent: "from-[#1d8efe] to-[#033880]",
        eyebrow: "At handover",
        eyebrowColor: "#febd59",
        LucideIcon: ClipboardCheck,
      },
    ],
    columns: [
      {
        title: "Cornerstone guides",
        links: [
          { name: "Adelaide build cost report 2026", href: "/adelaide-build-cost-report-2026" },
          { name: "Building costs Adelaide 2026", href: "/building-costs-adelaide-2026" },
          { name: "Complete building guide", href: "/complete-guide-building-adelaide" },
          { name: "Building a new home", href: "/building-a-new-home" },
          { name: "Knockdown rebuild guide", href: "/knockdown-rebuild" },
        ],
      },
      {
        title: "Process & costs",
        links: [
          { name: "Build timeline", href: "/building-a-new-home/build-timeline" },
          { name: "Contracts guide", href: "/building-a-new-home/contracts" },
          { name: "Budget guide", href: "/building-a-new-home/budget" },
          { name: "Renovation costs", href: "/renovations/costs" },
          { name: "Knockdown rebuild costs", href: "/knockdown-rebuild/costs" },
        ],
      },
      {
        title: "Reference",
        links: [
          { name: "Watch: Home Building Podcast", href: "/watch" },
          { name: "Building glossary", href: "/terminology" },
          { name: "Building tips & tricks", href: "/buildingtipsandtricks" },
          { name: "Help & advice", href: "/help" },
          { name: "Real Adelaide build stories", href: "/case-studies" },
          { name: "Editorial process", href: "/about/editorial-process" },
        ],
      },
      {
        title: "Material guides (Learn)",
        links: [
          { name: "All material guides", href: "/learn" },
          { name: "Furnishing your home", href: "/learn/furnishing-your-home" },
          { name: "Bedroom furnishing", href: "/learn/bedroom-furnishing" },
          { name: "Home office", href: "/learn/home-office" },
          { name: "Kitchen & cookware", href: "/learn/kitchen-and-cookware" },
          { name: "Tableware & glassware", href: "/learn/tableware-and-glassware" },
          { name: "Pergolas & outdoor living", href: "/learn/pergolas-and-outdoor-living" },
          { name: "Tiles guide", href: "/learn/tiles" },
          { name: "Flooring guide", href: "/learn/flooring" },
          { name: "Kitchens guide", href: "/learn/kitchens" },
          { name: "Bathrooms guide", href: "/learn/bathrooms" },
          { name: "Roofing guide", href: "/learn/roofing" },
        ],
      },
    ],
  },
  {
    name: "Building Types",
    href: "/journeys",
    circleStrip: buildingTypes.map((t) => ({ name: t.name, href: t.href, iconSrc: t.icon })),
    columns: [
      {
        title: "By project journey",
        links: [
          { name: "New home build", href: "/building-a-new-home", description: "Start-to-finish for a brand-new home in Adelaide." },
          { name: "Knockdown rebuild", href: "/knockdown-rebuild", description: "Demolish and rebuild on the same block." },
          { name: "House and land package", href: "/house-and-land-packages-adelaide", description: "Land + build bundled in growth corridors." },
          { name: "First home buyer", href: "/first-home-buyer-adelaide", description: "Grants, stamp duty savings and what to tackle first." },
          { name: "Renovation", href: "/renovations", description: "Quick freshen-up vs a proper renovation - what’s worth doing." },
          { name: "Invest or develop", href: "/property-development", description: "Splitting a block, two homes on one block, rental builds." },
          { name: "Changing builders", href: "/changing-builders", description: "How to exit and switch mid-project." },
        ],
      },
      {
        title: "By home type",
        links: [
          { name: "Custom home", href: "/custom-home-builders-adelaide", description: "Architectural, design-led builds." },
          { name: "Townhouse", href: "/townhouse-builders-adelaide", description: "Multi-dwelling and townhouse specialists." },
          { name: "Granny flat", href: "/granny-flat-builders-adelaide", description: "A second small home out the back - for family or rent." },
          { name: "Dual occupancy", href: "/dual-occupancy-adelaide", description: "Two separate homes on the one block, done well." },
          { name: "Country home", href: "/country-home-builders-south-australia", description: "Regional and rural SA builds." },
          { name: "Two storey home", href: "/best-builder-for-two-storey-homes", description: "Builders who do upper levels properly." },
          { name: "Acreage home", href: "/best-builder-for-acreage", description: "Acreage and large-block specialists." },
        ],
      },
      {
        title: "Design & planning",
        links: [
          { name: "Choosing a block of land", href: "/choosing-land", description: "Soil, slope, water/power and what the council allows." },
          { name: "Home design", href: "/home-design", description: "Layouts, orientation, room planning." },
          { name: "Interior design", href: "/interior-design", description: "Finishes, fixtures and inclusions." },
          { name: "Single vs double storey", href: "/single-vs-double-storey", description: "Costs, layouts and block size factors." },
          { name: "Building vs buying", href: "/building-vs-buying", description: "When new build beats existing." },
        ],
      },
      {
        title: "Outdoor & extras",
        links: [
          { name: "Verandahs or pergolas", href: "/verandahs-pergolas", description: "Outdoor entertaining add-ons." },
          { name: "Landscaping", href: "/landscaping", description: "Lawns, gardens, paving and outdoor areas for new builds." },
          { name: "Sheds or garages", href: "/sheds-garages", description: "Detached structures and garages." },
          { name: "Decking", href: "/decking", description: "Timber and composite decking options." },
          { name: "Swimming pools", href: "/swimming-pools", description: "Pool builds and integration with the home." },
        ],
      },
      {
        title: "Home improvement guides",
        links: [
          { name: "Home improvement hub", href: "/home-improvement", description: "All four project types, plus cost, choosing and planning guides." },
          { name: "Pool costs", href: "/swimming-pools/costs" },
          { name: "Choosing a pool builder", href: "/swimming-pools/choosing-a-builder" },
          { name: "Pool planning & permits", href: "/swimming-pools/planning" },
          { name: "Decking costs", href: "/decking/costs" },
          { name: "Choosing a deck builder", href: "/decking/choosing-a-builder" },
          { name: "Decking planning & permits", href: "/decking/planning" },
          { name: "Landscaping costs", href: "/landscaping/costs" },
          { name: "Choosing a landscaper", href: "/landscaping/choosing-a-landscaper" },
          { name: "Landscaping planning & permits", href: "/landscaping/planning" },
          { name: "Verandah costs", href: "/verandahs-pergolas/costs" },
          { name: "Choosing a verandah builder", href: "/verandahs-pergolas/choosing-a-builder" },
          { name: "Verandah planning & permits", href: "/verandahs-pergolas/planning" },
        ],
      },
    ],
  },
];

// VIC visitors get Melbourne-specific Builders menu columns (categories +
// growth corridors/suburbs). Only applied when state is confirmed VIC -
// crawlers / unknown / SA visitors keep the Adelaide default below, which
// protects Adelaide SEO. Hero buttons + build-type circle strip are
// state-agnostic (they route to the tool / learn pages) so they stay as-is.
const VIC_BUILDERS_COLUMNS = [
  {
    title: "Find a builder",
    links: [
      { name: "Best home builders Melbourne", href: "/home-builders-melbourne" },
      { name: "Melbourne builders directory", href: "/melbourne-builders" },
      { name: "New home builders Melbourne", href: "/new-home-builders-melbourne" },
      { name: "Our partner builders", href: "/building-partners" },
      { name: "Builder complaints (VIC)", href: "/builder-complaints-melbourne" },
    ],
  },
  {
    title: "By builder type",
    links: [
      { name: "Custom home builders", href: "/custom-home-builders-melbourne" },
      { name: "Volume home builders", href: "/volume-home-builders-melbourne" },
      { name: "Luxury home builders", href: "/luxury-home-builders-melbourne" },
      { name: "Budget home builders", href: "/budget-home-builders-melbourne" },
      { name: "Volume vs custom", href: "/volume-vs-custom-builder-melbourne" },
    ],
  },
  {
    title: "Builder issues (VIC)",
    links: [
      { name: "VBA builder registration", href: "/vba-builder-registration-melbourne" },
      { name: "Domestic Building Insurance", href: "/dbi-melbourne-explainer" },
      { name: "Building contracts", href: "/building-contracts-melbourne-guide" },
      { name: "Warranty after handover", href: "/warranty-and-after-handover-melbourne" },
      { name: "Fixed price vs cost plus", href: "/fixed-price-vs-cost-plus-melbourne" },
    ],
  },
  {
    title: "By growth corridor",
    links: [
      { name: "Wyndham corridor", href: "/home-builders-wyndham" },
      { name: "Melton corridor", href: "/home-builders-melton" },
      { name: "Casey corridor", href: "/home-builders-casey" },
      { name: "Hume corridor", href: "/home-builders-hume" },
      { name: "Whittlesea corridor", href: "/home-builders-whittlesea" },
      { name: "Cardinia corridor", href: "/home-builders-cardinia" },
      { name: "Compare all corridors", href: "/melbourne-growth-corridors" },
      { name: "All Melbourne growth suburbs", href: "/growth-suburbs-melbourne" },
      { name: "Building in Melbourne", href: "/building-in-melbourne" },
    ],
  },
];

// Returns a state-adapted nav. For confirmed VIC visitors the Builders menu
// columns are swapped for Melbourne equivalents; everything else is untouched.

// VIC "Explore Home & Land" overrides - mirrors the Adelaide layout but
// points every link at the Melbourne equivalents so VIC visitors see local
// estates/suburbs. Only applied for confirmed VIC visitors (navForState).
const VIC_ESTATE_TILES = [
  { name: 'Woodlea', locality: 'Rockbank', href: '/building-in-melbourne/estate/woodlea', accent: 'from-[#1d8efe] to-[#1670cc]', img: 'https://images.unsplash.com/photo-1524813686514-a57563d77965?w=500&q=70&auto=format&fit=crop', logo: 'https://www.google.com/s2/favicons?sz=128&domain=woodlea.com.au' },
  { name: 'Katalia', locality: 'Donnybrook', href: '/building-in-melbourne/estate/katalia', accent: 'from-[#4abff2] to-[#1d8efe]', img: 'https://images.unsplash.com/photo-1600645174997-bea67efe2fd1?w=500&q=70&auto=format&fit=crop', logo: 'https://www.google.com/s2/favicons?sz=128&domain=stockland.com.au' },
  { name: 'Kinbrook', locality: 'Donnybrook', href: '/building-in-melbourne/estate/kinbrook', accent: 'from-[#1d8efe] to-[#033880]', img: 'https://images.pexels.com/photos/15813739/pexels-photo-15813739.jpeg?auto=compress&cs=tinysrgb&w=500', logo: 'https://www.google.com/s2/favicons?sz=128&domain=kinbrook.com.au' },
  { name: 'Smiths Lane', locality: 'Clyde North', href: '/building-in-melbourne/estate/smiths-lane', accent: 'from-[#0ea5e9] to-[#1d8efe]', img: 'https://images.unsplash.com/photo-1579678929710-862f8e01c0b2?w=500&q=70&auto=format&fit=crop', logo: 'https://www.google.com/s2/favicons?sz=128&domain=frasersproperty.com.au' },
  { name: 'Officer Fields', locality: 'Officer', href: '/building-in-melbourne/estate/officer-fields', accent: 'from-[#1670cc] to-[#1d8efe]', img: 'https://images.unsplash.com/photo-1659684382258-57e192658f0d?w=500&q=70&auto=format&fit=crop', logo: 'https://www.google.com/s2/favicons?sz=128&domain=officerfields.com.au' },
  { name: 'Kaylana', locality: 'Tarneit', href: '/building-in-melbourne/estate/kaylana', accent: 'from-[#4abff2] to-[#1670cc]', img: 'https://images.pexels.com/photos/37079556/pexels-photo-37079556.jpeg?auto=compress&cs=tinysrgb&w=500', logo: 'https://www.google.com/s2/favicons?sz=128&domain=pottergeorge.com.au' },
];

const VIC_EXPLORE_HERO = [
  {
    title: "Home & Land Packages",
    subtitle: "Live BuildPilot inventory · Melbourne",
    href: "/house-and-land-melbourne",
    accent: "from-[#1d8efe] to-[#1670cc]",
    eyebrow: "Live now",
    eyebrowColor: "#febd59",
  },
  {
    title: "Melbourne growth corridors",
    subtitle: "Compare Wyndham, Casey, Cardinia & more",
    href: "/melbourne-growth-corridors",
    accent: "from-[#4abff2] to-[#1d8efe]",
    eyebrow: "Where to build",
    eyebrowColor: "#febd59",
  },
  {
    title: "Repayment Calculator",
    subtitle: "Powered by Equable · Indicative weekly / monthly figures",
    href: "/p/UAklUjJ9rbMGQITCzutU5tSrPtMF_ky9_2L4aP4TScs?token=eq_bp_2026_UAklUjJ9rbMGQITCzutU5tSrPtMF",
    accent: "from-[#1d8efe] to-[#033880]",
    eyebrow: "Powered by Equable",
    eyebrowColor: "#febd59",
  },
];

const VIC_EXPLORE_COLUMNS = [
  {
    title: "Browse corridors",
    links: [
      { name: "All Melbourne growth suburbs", href: "/growth-suburbs-melbourne" },
      { name: "Wyndham corridor", href: "/home-builders-wyndham" },
      { name: "Casey corridor", href: "/home-builders-casey" },
      { name: "Cardinia corridor", href: "/home-builders-cardinia" },
      { name: "Whittlesea corridor", href: "/home-builders-whittlesea" },
    ],
  },
  {
    title: "Guides & land",
    links: [
      { name: "Building in Melbourne", href: "/building-in-melbourne" },
      { name: "Compare growth corridors", href: "/melbourne-growth-corridors" },
      { name: "Melbourne estates & suburbs", href: "/building-in-melbourne" },
      { name: "Choosing land", href: "/choosing-land" },
    ],
  },
  {
    title: "Live Home & Land packages",
    links: [
      { name: "All live packages →", href: "/house-and-land-melbourne" },
      { name: "Melbourne region", href: "/house-and-land-melbourne/melbourne" },
      { name: "Geelong region", href: "/house-and-land-melbourne/geelong" },
      { name: "Regional Victoria", href: "/house-and-land-melbourne/regional-vic" },
    ],
  },
];

const navForState = (userState) => {
  if (userState !== 'VIC') return navConfig;
  return navConfig.map((item) => {
    if (item.name === 'Builders') return { ...item, columns: VIC_BUILDERS_COLUMNS };
    if (item.name === 'Explore Home & Land') return {
      ...item,
      href: '/house-and-land-melbourne',
      heroButtons: VIC_EXPLORE_HERO,
      columns: VIC_EXPLORE_COLUMNS,
      estateTiles: VIC_ESTATE_TILES,
      estateTilesViewAll: { href: '/building-in-melbourne', label: 'Explore all Melbourne estates' },
    };
    return item;
  });
};

const Header = ({ variant }) => {
  const userState = useUserState();
  const nav = useMemo(() => navForState(userState), [userState]);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const [openMobileDept, setOpenMobileDept] = useState(null);
  // MiniMatcher state, driven by hero-button clicks inside the mega menus.
  // `matcherState` holds the category + heading we want to launch with,
  // or null when the matcher is closed. Closing resets to null.
  const [matcherState, setMatcherState] = useState(null);
  // MiniHnlMatcher is a separate H&L-only flow with its own 3-question
  // quiz against /api/packages, so it gets its own open flag.
  const [hnlMatcherOpen, setHnlMatcherOpen] = useState(false);
  const [homeDesignsMatcherOpen, setHomeDesignsMatcherOpen] = useState(false);
  const closeTimer = useRef(null);
  const { openModal } = useContactModal();

  // 2026-06-05: Global "open matcher" custom-event bridge. Lets any
  // component (workspace cards, recommendation strips, smart triggers)
  // launch the Header-mounted MiniMatcher without prop drilling. Used
  // primarily to route New Home / Renovation / Home-Improvement builder
  // entry points through the matcher BEFORE the deeper FindABuilder
  // workspace tool.
  //
  // Usage from anywhere:
  //   window.dispatchEvent(new CustomEvent('buildpilot:open-matcher', {
  //     detail: { category, heading, subheading, categoryOptions,
  //               sourcePlacement }
  //   }));
  useEffect(() => {
    const handle = (e) => {
      const d = (e && e.detail) || {};
      setMatcherState({
        category: d.category || null,
        categoryOptions: d.categoryOptions || null,
        heading: d.heading || null,
        subheading: d.subheading || null,
        sourcePlacement: d.sourcePlacement || 'global-event',
        // 2026-02-28: honour optional `state` + `subcategory` in the
        // CustomEvent so `/matcher?category=...&state=SA` deep-links
        // can drop straight into the correct triage without asking
        // the user to re-pick their state.
        initialState: d.state || null,
        initialSubcategory: d.subcategory || null,
      });
    };
    window.addEventListener('buildpilot:open-matcher', handle);
    return () => window.removeEventListener('buildpilot:open-matcher', handle);
  }, []);

  // 2026-02-27: Consume the `/matcher` route hand-off flag. When users
  // land on `/matcher` (a phantom route used across pillar CTAs), the
  // MatcherRoute component redirects to `/home-builders-adelaide` and
  // drops a session flag. On the next Header mount, we dispatch the
  // same global `buildpilot:open-matcher` event so the MiniMatcher
  // opens over the destination page. This does NOT touch the matcher
  // itself and only reuses the existing event contract.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem('bp:openMatcherOnLoad');
      if (!raw) return;
      window.sessionStorage.removeItem('bp:openMatcherOnLoad');
      const parsed = JSON.parse(raw) || {};
      // Only honour flags dropped in the last 10 seconds so a stale
      // flag from a previous tab cannot pop the matcher unexpectedly.
      if (parsed.ts && (Date.now() - parsed.ts) > 10000) return;
      // 2026-11-03: shareable audience URLs (/find/builder|supplier|trade|
      // professional) drop a serialisable `audience` key. Map it here to
      // the full matcher (categoryOptions + heading) so the option-picker
      // icons never need serialising through sessionStorage.
      const AUDIENCE_MATCHERS = {
        builder: BUILDERS_MATCHER,
        supplier: SUPPLIERS_MATCHER,
        trade: TRADES_MATCHER,
        professional: PROS_MATCHER,
      };
      const audMatcher = parsed.audience ? AUDIENCE_MATCHERS[parsed.audience] : null;
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('buildpilot:open-matcher', {
          detail: audMatcher ? {
            ...audMatcher,
            sourcePlacement: parsed.sourcePlacement || 'find-audience-url',
          } : {
            category: parsed.category || 'professional:builder',
            // 2026-02-28: forward optional subcategory + state so
            // /matcher?category=supplier:solar_products&state=SA drops
            // straight into the correct triage flow without asking
            // the user to re-pick their state.
            ...(parsed.subcategory ? { subcategory: parsed.subcategory } : {}),
            ...(parsed.state ? { state: parsed.state } : {}),
            // 2026-02-28: contextual copy from pillar CTAs (e.g.
            // "Shortlist your Adelaide pool builder"). MiniMatcher
            // already accepts `heading` + `subheading` overrides -
            // this just plumbs them through the /matcher deep-link.
            ...(parsed.heading ? { heading: parsed.heading } : {}),
            ...(parsed.subheading ? { subheading: parsed.subheading } : {}),
            sourcePlacement: parsed.sourcePlacement || 'matcher-route',
          },
        }));
      }, 250);
    } catch { /* sessionStorage or JSON parse failure - ignore */ }
  }, []);

  // 2026-feb: Sibling event for opening the H&L MiniMatcher. Used
  // by the "Find a Builder" matcher's land-handoff branch when a
  // user answers "No, looking for land" and opts to review H&L
  // packages - we close the builder popup and pop this one in its
  // place for a seamless transition.
  useEffect(() => {
    const handle = () => setHnlMatcherOpen(true);
    window.addEventListener('buildpilot:open-hnl-matcher', handle);
    return () => window.removeEventListener('buildpilot:open-hnl-matcher', handle);
  }, []);

  // 2026-02-17: Sibling event for opening the Home Designs (Floor Plans)
  // Mini Matcher. Used by the homepage ShortlistStartersSection so the
  // "Floor Plans" pill can launch the mini matcher without prop-drilling
  // setHomeDesignsMatcherOpen into every consumer.
  useEffect(() => {
    const handle = () => setHomeDesignsMatcherOpen(true);
    window.addEventListener('buildpilot:open-home-designs-matcher', handle);
    return () => window.removeEventListener('buildpilot:open-home-designs-matcher', handle);
  }, []);

  // Preload mega-menu icons once so there's no flash/delay the first
  // time the Building Types menu opens.
  useEffect(() => {
    buildingTypes.forEach((t) => {
      if (!t.icon) return;
      const img = new Image();
      img.src = t.icon;
    });
  }, []);

  // Mobile-only: mirror desktop's natural "header scrolls away with the
  // page" behaviour. On iOS Safari, focusing an input doesn't actually
  // scroll the document - it just shrinks the visual viewport and pans
  // to keep the input visible, so a position:absolute header at top:0
  // stays on screen indefinitely. To match desktop UX exactly, we
  // instantly display:none the header while any text input is focused
  // on a mobile-sized viewport (<1024px). No animation.
  const [inputFocused, setInputFocused] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const isMobile = () => window.innerWidth < 1024;
    const isEditableTarget = (t) => {
      if (!t) return false;
      const tag = t.tagName?.toLowerCase();
      return tag === 'input' || tag === 'textarea' || t.isContentEditable === true;
    };
    const onFocusIn = (e) => {
      if (!isMobile()) return;
      if (isEditableTarget(e.target)) setInputFocused(true);
    };
    const onFocusOut = () => {
      setTimeout(() => {
        if (!isEditableTarget(document.activeElement)) setInputFocused(false);
      }, 60);
    };
    const onResize = () => {
      if (!isMobile()) setInputFocused(false);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // Publish the live header height to CSS so every page can size its
  // hero padding relative to whatever the header actually is right
  // now (announcement bars, smart banners, Row 2 nav, mobile sizes,
  // etc. all affect it). Pages consume:
  //   --bp-header-h    raw measured height
  //   --bp-hero-top    header + 40px clearance, the "use this for the
  //                    top padding of your hero section" var.
  //
  // Why this matters: hard-coded `lg:pt-44` etc. break whenever the
  // header height changes (e.g. we remove a banner, or add an
  // announcement bar). With this var, every page adapts on its own.
  // ─────────────────────────────────────────────────────────────────
  const headerEl = useRef(null);
  useEffect(() => {
    const el = headerEl.current;
    if (!el) return undefined;
    const publish = () => {
      const h = el.getBoundingClientRect().height;
      if (!h) return;
      const root = document.documentElement;
      // Floor to the known-good baseline for the current breakpoint so a
      // transient/early measurement (e.g. Row 2 nav or web fonts not laid
      // out yet) can never publish a value that sits SHORTER than the real
      // header - that was letting hero content slide up under the menu.
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const minHeader = isDesktop ? 138 : 78;
      const minHeroTop = isDesktop ? 178 : 118;
      const headerH = Math.max(Math.round(h), minHeader);
      root.style.setProperty('--bp-header-h', `${headerH}px`);
      // 40px clearance below the menu by default. The "+40" is a
      // visual breathing constant, tweak here if every hero feels
      // tight/loose globally.
      root.style.setProperty('--bp-hero-top', `${Math.max(headerH + 40, minHeroTop)}px`);
    };
    publish();
    // Re-measure once layout + fonts have settled so a tall two-row header
    // is captured accurately (not just the first-paint single-row height).
    const raf = requestAnimationFrame(() => requestAnimationFrame(publish));
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(publish).catch(() => {});
    }
    window.addEventListener('load', publish);
    // ResizeObserver catches: announcement-bar toggle, smart-banner
    // mount/unmount, mobile rotation, font swap, etc.
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    window.addEventListener('resize', publish);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', publish);
      window.removeEventListener('load', publish);
    };
  });

  // Auto-detect dark-hero pages so the logo + menu always stay readable.
  // Routes here have a dark `#1d1d1f` hero or photographic dark hero. The
  // Header is force-rendered with white text/logo on these routes; the
  // explicit `variant` prop only takes effect on other routes.
  //
  // IMPORTANT: only add a route if its hero IS actually dark. Pages with
  // light gradient heroes (`from-[#f5f7fa] to-white`) or `bg-white`
  // heroes must NOT be listed - they need dark text to stay readable.
  const DARK_HERO_PATTERNS = [
    /^\/$/,                                  // Homepage (dark video hero)
    // 2026-02-28: /builders swapped to HubHeroDark variant="light"
    // 2026-02-28: /designs swapped to HubHeroDark variant="light"
    // 2026-02-28: /suburbs unchanged
    /^\/suburbs\/?$/,                        // SuburbsHub (#1d1d1f)
    // 2026-02-28: /suppliers swapped to HubHeroDark variant="light"
    // 2026-02-28: /house-and-land-adelaide swapped to HubHeroDark variant="light"
    // 2026-02-28: /land-for-sale-adelaide swapped to HubHeroDark variant="light"
    // 2026-02-28: /find/category/{tradies,professional-services} swapped to variant="light"
    // 2026-02-28: /guides and /journeys swapped to variant="light"
    /^\/one-on-one-support\/?$/,             // HeroDark
    /^\/house-and-land-packages-adelaide\/?$/, // HeroDark (legacy route)
    /^\/where-we-fit-in\/?$/,                // Photographic dark hero
    /^\/why-buildpilot\/?$/,                 // Photographic dark hero
    /^\/register-business\/?$/,              // HubHeroDark hero (business onboarding)
    // Building type pages (BuildingTypePage component) all use a
    // photographic hero with from-black/80 overlay. Added 2026-06-03
    // after user reported the logo/menu invisible on /landscaping.
    /^\/renovations\/?$/,
    /^\/home-improvement\/?$/,
    /^\/changing-builders\/?$/,
    /^\/property-development\/?$/,
    /^\/knockdown-rebuild\/?$/,
    /^\/verandahs-pergolas\/?$/,
    /^\/landscaping\/?$/,
    /^\/sheds-garages\/?$/,
    /^\/decking\/?$/,
    /^\/swimming-pools\/?$/,
    // Marketing pages with photographic dark hero (lazy-loaded image
    // + `from-black/70` overlay). Logo/menu must flip to white.
    /^\/blog\/?$/,
    /^\/case-studies(\/.*)?$/,            // Index + slug detail (dark hero)
    // Note: /builders/<slug>, /journeys, /journeys/<slug>, /community
    // all have LIGHT gradient heroes - they default to dark text.
    //
    // Legal pages with `from-[#1d1d1f]` Trust & Safety hero.
    // Added 2026-06-05 after user reported black logo/menu on dark hero.
    /^\/terms\/?$/,
    /^\/privacy\/?$/,
    /^\/copyright\/?$/,
    /^\/cookies\/?$/,
    /^\/legal\/matching-policy\/?$/,
    /^\/legal\/complaints-process\/?$/,
    /^\/legal\/reviews-policy\/?$/,
    // Editorial / search pages with dark hero. NB: `/guides` is NOT here -
    // that route renders <HubPage slug="guides"> which has a LIGHT hero.
    /^\/adelaide-build-cost-report-2026\/?$/,
    // Melbourne/VIC parity pages with navy (#0b1f3a) hero. Added
    // 2026-06 after user reported dark logo/menu on the dark hero.
    /^\/build-times-melbourne\/?$/,
    /^\/house-building-costs-by-bedroom-melbourne\/?$/,
    /^\/search\/?$/,
    /^\/builder-selection-checklist\/?$/,
    // 2026-02-28: /guides and /journeys swapped to HubHeroDark variant="light"
    /^\/land-estate(\/.*)?$/,             // Index + slug detail (dark hero)
    // Builder type / comparison / regional cluster pages with dark hero
    /^\/small-home-builders-adelaide\/?$/,
    /^\/transportable-homes-adelaide\/?$/,
    /^\/reliable-builders-adelaide\/?$/,
    /^\/build-vs-buy-adelaide\/?$/,
    /^\/growth-suburbs-adelaide\/?$/,
    // Home designs hub + builder-specific designs pages (dark hero)
    /^\/home-designs(\/.*)?$/,
    // Unified BusinessProfilePage routes (dark #1d1d1f hero). Added
    // 2026-02-22 after user reported invisible logo + menu text on
    // partner profile pages (e.g. /professionals/fairmont-homes).
    /^\/professionals\/[^/]+\/?$/,
    /^\/tradies\/[^/]+\/?$/,
    /^\/business\/[^/]+\/?$/,
    // Legacy builder + supplier detail pages (BuilderSpotlight,
    // SupplierDetailPage) also render a dark #1d1d1f hero. Added at
    // the same time so all four partner-profile route shapes flip
    // the header consistently.
    /^\/builders\/[^/]+\/?$/,
    /^\/suppliers\/[^/]+\/?$/,
    // Find super-category hub pages (Tradies / Pro Services / Suppliers
    // / Builders) used to render HubHeroDark with dark bg. 2026-02-28:
    // swapped to variant="light" so they no longer need to flip the
    // header. Pattern intentionally removed.
  ];
  const isDarkHeroRoute = DARK_HERO_PATTERNS.some((p) => p.test(location.pathname));

  // ─── Auto-detect dark backgrounds behind the header ──────────────
  // The DARK_HERO_PATTERNS list above is a hand-maintained allow-list
  // and inevitably misses newly added dark-hero pages, leaving a black
  // logo + black menu unreadable on a dark background. This measures the
  // ACTUAL rendered background brightness directly behind the header on
  // every route change and flips the header to white when it's dark.
  //
  // Safety: detection can ONLY add white-logo pages (set autoDark=true).
  // It never forces dark text, so a page the list already handles can
  // never be regressed by a mis-read.
  const [autoDark, setAutoDark] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (location.pathname.startsWith('/workspace')) { setAutoDark(false); return undefined; }

    const parseRGB = (str) => {
      if (!str) return null;
      const m = str.match(/rgba?\(([^)]+)\)/i);
      if (!m) return null;
      const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
      const [r, g, b] = parts;
      const a = parts.length >= 4 ? parts[3] : 1;
      if ([r, g, b].some((n) => Number.isNaN(n))) return null;
      return { r, g, b, a: Number.isNaN(a) ? 1 : a };
    };
    // Composite colour `c` (may be semi-transparent) over opaque `bg`.
    const over = (bg, c) => {
      if (!c || c.a <= 0) return bg;
      const a = c.a;
      return {
        r: c.r * a + bg.r * (1 - a),
        g: c.g * a + bg.g * (1 - a),
        b: c.b * a + bg.b * (1 - a),
      };
    };
    // Average the colour stops of a CSS gradient into one rgba.
    const avgGradient = (bgImage) => {
      const tokens = bgImage.match(/rgba?\([^)]+\)/gi);
      if (!tokens || !tokens.length) return null;
      let r = 0, g = 0, b = 0, a = 0, n = 0;
      tokens.forEach((t) => {
        const c = parseRGB(t);
        if (c) { r += c.r; g += c.g; b += c.b; a += c.a; n += 1; }
      });
      if (!n) return null;
      return { r: r / n, g: g / n, b: b / n, a: a / n };
    };
    const lum = (c) => (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;

    // Effective SOLID background brightness (0 dark … 1 light) at a point.
    // Only composites real colours + gradients - never guesses at the
    // brightness of a photographic <img>/url() (those are judged via the
    // hero heading's text colour instead, which is far more reliable).
    const bgBrightnessAt = (x, y) => {
      let els;
      try { els = document.elementsFromPoint(x, y); } catch { return null; }
      if (!els || !els.length) return null;
      const headerNode = headerEl.current;
      const stack = els
        .filter((el) => !(headerNode && headerNode.contains(el)))
        .reverse();
      let acc = { r: 255, g: 255, b: 255 };
      let sawPaint = false;
      for (const el of stack) {
        const cs = window.getComputedStyle(el);
        const solid = parseRGB(cs.backgroundColor);
        if (solid && solid.a > 0) { acc = over(acc, solid); sawPaint = true; }
        const bi = cs.backgroundImage;
        if (bi && bi !== 'none' && /gradient/i.test(bi)) {
          const grad = avgGradient(bi);
          if (grad) { acc = over(acc, grad); sawPaint = true; }
        }
      }
      if (!sawPaint) return null;
      return lum(acc);
    };

    // Primary signal: the hero heading's text colour. Designers pick
    // light text for dark backgrounds and dark text for light ones, so
    // the <h1> colour is a direct, reliable read of how dark the hero
    // behind the header is. Returns true (dark bg) / false (light bg) /
    // null (unknown).
    const headingSignalsDark = () => {
      const nodes = document.querySelectorAll('h1');
      for (const el of nodes) {
        // Ignore headings inside modals / dialogs (e.g. the location gate)
        // so a popup title can never be mistaken for the hero heading.
        if (el.closest('[role="dialog"], [aria-modal="true"]')) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width < 40 || rect.height < 10) continue; // hidden / tiny
        if (rect.top > window.innerHeight) continue;        // below the fold
        const col = parseRGB(window.getComputedStyle(el).color);
        if (!col) return null;
        const l = lum(col);
        if (l > 0.7) return true;  // light heading  → dark background
        if (l < 0.4) return false; // dark heading   → light background
        return null;               // mid-tone heading → ambiguous
      }
      return null; // no hero heading on screen
    };

    const detect = () => {
      let dark = false;
      const h = headingSignalsDark();
      if (h === true) dark = true;
      // Corroborate / catch solid dark heroes that have no hero heading.
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vals = [Math.round(vw * 0.08), Math.round(vw * 0.5)]
        .map((x) => bgBrightnessAt(x, 44))
        .filter((v) => v !== null);
      let bgAvg = null;
      if (vals.length) {
        bgAvg = vals.reduce((s, v) => s + v, 0) / vals.length;
        if (bgAvg < 0.4) dark = true;
      }
      // Only commit once something is actually known, so a premature read
      // of a still-blank page can't force the logo back to black.
      if (h !== null || bgAvg !== null) setAutoDark(dark);
    };

    setAutoDark(false); // reset on navigation before re-measuring
    const t0 = requestAnimationFrame(() => requestAnimationFrame(detect));
    const t1 = setTimeout(detect, 350);
    const t2 = setTimeout(detect, 900);
    window.addEventListener('load', detect);
    return () => {
      cancelAnimationFrame(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('load', detect);
    };
  }, [location.pathname]);


  // ─── Workspace mode ──────────────────────────────────────────────
  // When the marketing header is rendered inside the authenticated
  // workspace (/workspace/*) we anchor it inline (not absolute), give
  // it a solid white surface so feed content can scroll cleanly beneath,
  // hide the announcement bar, and add workspace affordances (email +
  // notification bell) to the right cluster.
  const isWorkspaceRoute = location.pathname.startsWith('/workspace');
  const [emailFwdOpen, setEmailFwdOpen] = useState(false);

  // Resolution order: dark-hero route ALWAYS wins (white text/logo),
  // workspace route always uses dark text on white surface, otherwise
  // honour explicit `variant="light"` (white text), default = dark text.
  // autoDark (runtime-measured) can only ADD a white-logo page; it never
  // forces dark text, so list-covered pages can't be regressed by a mis-read.
  const forceWhite = isDarkHeroRoute || autoDark;
  const useDarkText = isWorkspaceRoute ? true : (forceWhite ? false : variant !== 'light');

  const handleGetInTouch = () => {
    setMobileMenuOpen(false);
    openModal({ service: '', source: 'Header', prefilledMessage: '' });
  };

  // Marketing-side auth signal. Lets the header swap a Sign-in CTA for an
  // avatar chip when the visitor is already logged into BuildPilot.
  const auth = useMarketingAuth();
  const authModal = useAuthModal();
  const navigate = useNavigate();
  // Workspace notifications, read straight from Supabase via the shared
  // hook (now safe to call from the marketing header because the
  // AuthProvider has been lifted to the App root). We only need the
  // unread count for the dropdown badge.
  const { items: notifItems } = useWorkspaceNotifications({ limit: 8 });
  const unreadNotifCount = auth.isLoggedIn
    ? notifItems.filter((n) => !n.read).length
    : 0;
  const goToDashboard = () => {
    setMobileMenuOpen(false);
    navigate('/workspace/dashboard');
  };
  // Stash the scroll position the instant the user starts pressing
  // the auth trigger button. Browsers perform their internal focus +
  // scroll-into-view behaviour during mousedown/pointerdown, BEFORE
  // React's onClick fires - so reading window.scrollY in the click
  // handler returns the already-changed value. Capturing on
  // pointerdown gets us the user's true visual position.
  const authReturnYRef = useRef(null);
  const captureAuthReturnY = () => {
    authReturnYRef.current = window.scrollY;
  };
  const openLoginModal = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setMobileMenuOpen(false);
    const y = authReturnYRef.current ?? window.scrollY;
    authModal.open('signin', { returnY: y });
    authReturnYRef.current = null;
  };
  const openSignupModal = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const y = authReturnYRef.current ?? window.scrollY;
    authModal.open('signup', { returnY: y });
    authReturnYRef.current = null;
  };

  // Logged-in account dropdown (desktop only). Opens on hover OR click;
  // closes on outside click, Esc, or 250ms after mouse leaves the chip
  // AND the menu surface. The grace period stops the menu from snapping
  // shut while the cursor is in transit between chip and menu items.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const accountCloseTimer = useRef(null);
  const cancelAccountClose = () => {
    if (accountCloseTimer.current) {
      clearTimeout(accountCloseTimer.current);
      accountCloseTimer.current = null;
    }
  };
  const scheduleAccountClose = () => {
    cancelAccountClose();
    accountCloseTimer.current = setTimeout(() => setAccountMenuOpen(false), 250);
  };
  useEffect(() => () => cancelAccountClose(), []);
  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        cancelAccountClose();
        setAccountMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setAccountMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountMenuOpen]);

  const handleSignOut = async () => {
    setAccountMenuOpen(false);
    try {
      const supabase = getSupabase();
      await supabase?.auth?.signOut();
    } catch (e) {
      // Even if sign-out fails server-side, the marketing auth hook will
      // pick up the next auth-state change and clear the chip.
      console.warn('[header] signOut failed:', e?.message);
    }
    navigate('/');
  };

  const handleOneOnOne = () => {
    setAccountMenuOpen(false);
    openModal({ service: '', source: 'Header dropdown', prefilledMessage: '' });
  };

  // Hover handling with small delay to prevent jitter when crossing the gap
  const handleEnter = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(name);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  // Close mega-menu on route change / escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // iOS Safari fix: the URL/search bar overlays the visual viewport on first
  // paint (and during scroll-collapse animation). `env(safe-area-inset-top)`
  // returns 0 in this state because the bar is browser chrome, not a notch.
  // We measure `visualViewport.offsetTop` directly and expose it as a CSS
  // variable so the announcement bar can dodge the bar at every frame.
  useEffect(() => {
    const root = document.documentElement;
    const updateVV = () => {
      const offset = window.visualViewport?.offsetTop ?? 0;
      root.style.setProperty('--vv-offset-top', `${Math.max(0, offset)}px`);
    };
    updateVV();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', updateVV);
    vv?.addEventListener('scroll', updateVV);
    window.addEventListener('orientationchange', updateVV);
    window.addEventListener('load', updateVV);
    // Re-measure shortly after mount - iOS Safari animates the URL bar in
    // the first ~600ms of page load and the visualViewport API can return
    // stale values during the animation.
    const t1 = setTimeout(updateVV, 250);
    const t2 = setTimeout(updateVV, 800);
    return () => {
      vv?.removeEventListener('resize', updateVV);
      vv?.removeEventListener('scroll', updateVV);
      window.removeEventListener('orientationchange', updateVV);
      window.removeEventListener('load', updateVV);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <>
    <MegaMenuIconPreloader />
    <header
      ref={headerEl}
      style={{
        paddingTop: 'max(env(safe-area-inset-top, 0px), var(--vv-offset-top, 0px))',
      }}
      className={`${
        isWorkspaceRoute
          ? 'relative z-30 bg-white border-b border-black/[0.06] bp-workspace-marketing-header bp-safe-area-top'
          : 'absolute top-0 left-0 right-0 z-50 bp-safe-area-top'
      } ${
        // 2026-02-28: removed the hide-header-on-input-focus rule
        // that was added as an iOS-keyboard scroll-away UX. Founder
        // reports the logo + Get-started button + hamburger vanishing
        // when the search field takes focus, which reads as broken.
        // If we want a scroll-away experience later, do it via
        // scroll-position not focus-state so the chrome stays visible
        // while the visitor is actually typing.
        ''
      }`}
    >
      {/* Announcement bar removed per product direction, the Download
          App pill + the FAB cover the same conversion intent without
          taking permanent header real estate. */}

      {/* Main Navigation */}
      <nav className="bg-transparent pt-4 sm:pt-5 lg:pt-6">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - eager + high fetch priority since it is the LCP
                on most brand-first routes. Sized down on mobile from
                34px to 28px so it stops crowding the CTA + burger. */}
            <Link to="/" className="flex items-center shrink-0 min-h-[44px] lg:min-h-0" data-testid="header-logo">
              <img
                src={useDarkText ? "/images/buildpilot-logo-2026-dark.png" : "/images/buildpilot-logo-2026.png"}
                alt="BuildPilot"
                width="140"
                height="32"
                className="h-[28px] sm:h-[32px] lg:h-[36px] w-auto"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </Link>

            {/* Desktop Row 1 right-side cluster: Download App + Sign in /
                Account. Nav items live on Row 2 below so the menu can
                spread evenly across the full width on desktop. */}
            <div className="hidden lg:flex items-center gap-1.5 xl:gap-2">
              <Link
                to="/register-business"
                className={`inline-flex items-center justify-center h-9 xl:h-10 px-4 xl:px-5 rounded-full text-[13px] xl:text-sm font-medium whitespace-nowrap transition-colors ${
                  useDarkText
                    ? 'text-[#1d1d1f] hover:bg-black/5'
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid="nav-register-business-btn"
              >
                Become a Partner
              </Link>
              <Link
                to="/one-on-one-support"
                className={`inline-flex items-center justify-center h-9 xl:h-10 min-w-[148px] xl:min-w-[160px] px-4 xl:px-5 rounded-full text-[13px] xl:text-sm font-medium whitespace-nowrap transition-colors border ${
                  useDarkText
                    ? 'text-[#1d1d1f] border-[#1d1d1f]/15 hover:bg-black/5'
                    : 'text-white border-white/30 hover:bg-white/10'
                }`}
                data-testid="nav-one-on-one-btn"
              >
                Talk to a real human
              </Link>
              <a
                href="https://buildpilot.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-9 xl:h-10 min-w-[148px] xl:min-w-[160px] px-4 xl:px-5 rounded-full text-[13px] xl:text-sm font-medium text-white gradient-text-purple-bg transition-all hover:opacity-90 whitespace-nowrap"
                data-testid="nav-download-app-btn"
              >
                Download App
              </a>
              {auth.isLoggedIn ? (
                <div
                  ref={accountMenuRef}
                  className="relative"
                  onMouseEnter={() => { cancelAccountClose(); setAccountMenuOpen(true); }}
                  onMouseLeave={scheduleAccountClose}
                >
                    <button
                      onClick={() => { cancelAccountClose(); setAccountMenuOpen((v) => !v); }}
                      className="relative inline-flex items-center justify-center gap-2 pl-1.5 pr-3.5 xl:pr-4 py-1 rounded-full text-[13px] xl:text-sm font-medium text-white bp-orange-glow-bg transition-all hover:opacity-95 shadow-[0_4px_14px_rgba(245,166,35,0.4)]"
                      data-testid="nav-dashboard-chip"
                      aria-haspopup="menu"
                      aria-expanded={accountMenuOpen}
                      aria-label={`Account menu - signed in as ${auth.firstName || 'BuildPilot user'}`}
                    >
                      <span
                        className="w-7 h-7 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-[12px] font-semibold"
                        aria-hidden="true"
                      >
                        {auth.initial}
                      </span>
                      <span className="hidden xl:inline truncate max-w-[140px]">
                        G'day {auth.firstName || 'there'}
                      </span>
                      <span className="xl:hidden">
                        G'day {auth.firstName || 'there'}
                      </span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${accountMenuOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                      {unreadNotifCount > 0 && (
                        <span
                          className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#ef4444] text-white text-[9px] font-semibold leading-none border-2 border-white"
                          aria-label={`${unreadNotifCount} unread notifications`}
                          data-testid="nav-dashboard-chip-badge"
                        >
                          {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                        </span>
                      )}
                    </button>

                    {accountMenuOpen && (
                      <div
                        role="menu"
                        onMouseEnter={cancelAccountClose}
                        onMouseLeave={scheduleAccountClose}
                        className="absolute right-0 top-full pt-2 z-50"
                        data-testid="nav-account-dropdown"
                      >
                        <div className="w-60 rounded-2xl bg-white/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(15,23,42,0.18)] border border-black/[0.06] py-2">
                        {/* Top of dropdown = iOS app tabs (1:1 with the
                            mobile BottomTabBar, same icons, same routes).
                            One label override: "More" reads as "Toolbox"
                            here per user direction (Feb 26 2026), since
                            the dropdown has the room and "More" feels
                            vague on desktop where every other item is
                            named explicitly. */}
                        {WORKSPACE_TABS.map((t) => {
                          const Icon = t.IconInactive;
                          const label = t.key === 'toolbox' ? 'Toolbox' : t.label;
                          return (
                            <Link
                              key={t.key}
                              to={t.to}
                              onClick={() => setAccountMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors"
                              role="menuitem"
                              data-testid={`nav-account-${t.key}`}
                            >
                              <span className="w-4 h-4 inline-flex items-center justify-center text-[#1d8efe]">
                                <Icon size={16} />
                              </span>
                              {label}
                            </Link>
                          );
                        })}
                        <div className="h-px bg-black/[0.06] my-1.5" aria-hidden="true" />
                        <Link
                          to="/workspace/notifications"
                          onClick={() => setAccountMenuOpen(false)}
                          className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13.5px] text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors"
                          role="menuitem"
                          data-testid="nav-account-notifications"
                        >
                          <span className="flex items-center gap-3">
                            <Bell className="w-4 h-4 text-[#1d8efe]" />
                            Notifications
                          </span>
                          {unreadNotifCount > 0 && (
                            <span
                              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#ef4444] text-white text-[10px] font-semibold leading-none"
                              data-testid="nav-account-notifications-badge"
                            >
                              {unreadNotifCount}
                            </span>
                          )}
                        </Link>
                        <button
                          type="button"
                          onClick={() => { setAccountMenuOpen(false); setEmailFwdOpen(true); }}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors w-full text-left"
                          role="menuitem"
                          data-testid="nav-account-email-forward"
                        >
                          <Mail className="w-4 h-4 text-[#1d8efe]" />
                          Email Forwarding
                        </button>
                        <div className="h-px bg-black/[0.06] my-1.5" aria-hidden="true" />
                        <Link
                          to="/workspace/profile"
                          onClick={() => setAccountMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors"
                          role="menuitem"
                          data-testid="nav-account-profile"
                        >
                          <UserIcon className="w-4 h-4 text-[#1d8efe]" />
                          Profile
                        </Link>
                        <Link
                          to="/workspace/settings"
                          onClick={() => setAccountMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors"
                          role="menuitem"
                          data-testid="nav-account-settings"
                        >
                          <SettingsIcon className="w-4 h-4 text-[#1d8efe]" />
                          Settings
                        </Link>
                        <Link
                          to="/workspace/billing"
                          onClick={() => setAccountMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors"
                          role="menuitem"
                          data-testid="nav-account-billing"
                        >
                          <Gem className="w-4 h-4 text-[#1d8efe]" />
                          Billing & Premium
                        </Link>
                        <SAOnly>
                          <button
                            type="button"
                            onClick={handleOneOnOne}
                            className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[#1d1d1f] hover:bg-[#f5f5f7] transition-colors w-full text-left"
                            role="menuitem"
                            data-testid="nav-account-oneonone"
                          >
                            <MessageCircle className="w-4 h-4 text-[#1d8efe]" />
                            Talk to a real human
                          </button>
                        </SAOnly>
                        <div className="h-px bg-black/[0.06] my-1.5" aria-hidden="true" />
                        <button
                          type="button"
                          onClick={handleSignOut}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13.5px] text-[#c0392b] hover:bg-[#f0f4fa] transition-colors w-full text-left"
                          role="menuitem"
                          data-testid="nav-account-signout"
                        >
                          <LogOut className="w-4 h-4" />
                          Log out
                        </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onPointerDown={captureAuthReturnY}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openLoginModal}
                    className="inline-flex items-center justify-center h-9 xl:h-10 min-w-[148px] xl:min-w-[160px] px-4 xl:px-5 rounded-full text-[13px] xl:text-sm font-medium text-white gradient-orange-bg transition-all hover:opacity-90 whitespace-nowrap"
                    data-testid="nav-sign-in-btn"
                  >
                    Sign in / Sign up
                  </button>
                )}
                {/* 2026-02-28: State context chip sits to the right of the
                    Sign in / account cluster. White chip with black text +
                    outlined state icon, opens a dropdown then a Notify-me
                    modal for the other 7 states. */}
                <StateContextChip />
            </div>

            {/* Mobile right-side cluster: account chip OR Get Started Today pill,
                plus the hamburger. Logged-out gets a compact orange pill (matches
                the desktop hero CTA); logged-in gets the avatar chip linking
                straight to the dashboard. State chip lives INSIDE the hamburger
                menu on mobile - it was too visually loud in the top row. */}
            <div className="lg:hidden flex items-center gap-2">
              {auth.isLoggedIn ? (
                <button
                  onClick={goToDashboard}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white bp-orange-glow-bg transition-colors"
                  aria-label="Open dashboard"
                  data-testid="mobile-dashboard-chip"
                >
                  <span className="text-[12px] font-semibold" aria-hidden="true">{auth.initial}</span>
                </button>
              ) : (
                <button
                  onPointerDown={captureAuthReturnY}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={openSignupModal}
                  className="inline-flex items-center justify-center bp-orange-glow-bg text-white px-3.5 h-10 rounded-full text-[12px] font-semibold whitespace-nowrap shadow-[0_4px_14px_rgba(245,166,35,0.4)]"
                  aria-label="Start your build today"
                  data-testid="mobile-start-today"
                >
                  Get Started Today
                </button>
              )}

              {/* Hamburger - unchanged behaviour, just relocated into this cluster */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${
                  useDarkText
                    ? 'border-[#1d1d1f]/30 text-[#1d1d1f] hover:bg-[#1d1d1f]/10'
                    : 'border-white/40 text-white hover:bg-white/10'
                }`}
                data-testid="mobile-menu-btn"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="18" x2="20" y2="18" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Desktop Row 2: nav items spread evenly across the full
              width, wrapped in a faint pill so the cluster reads as a
              single navigation surface. Faint white on dark heroes,
              faint black on light heroes. Tight padding so the total
              header height stays close to the single-row baseline. */}
          <div className="hidden lg:flex pt-1 pb-2">
            <div
              className={`flex-1 flex items-center justify-between gap-1 rounded-full px-2.5 py-1.5 bp-glass-navpill border ${
                useDarkText
                  ? 'bg-white/60 border-black/5 bp-glass-edge'
                  : 'bg-white/10 border-white/15 bp-glass-edge--dark bp-glass-navpill--hero'
              }`}
              data-testid="header-nav-row-pill"
            >
              {nav.map((item) => {
              const hasMenu = !!item.columns || !!item.productsMega;
              return (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => hasMenu && handleEnter(item.name)}
                  onMouseLeave={handleLeave}
                >
                  <Link
                    to={item.href}
                    onClick={() => setOpenMenu(null)}
                    className={`inline-flex items-center px-3.5 xl:px-5 py-2.5 text-[13px] xl:text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                      item.highlight
                        ? useDarkText
                          ? 'text-[#1d8efe] hover:text-[#1d1d1f]'
                          : 'text-white hover:bg-white/10'
                        : useDarkText
                          ? 'text-[#1d1d1f]/80 hover:text-[#1d1d1f] hover:bg-black/5'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                    } ${openMenu === item.name ? (useDarkText ? 'bg-black/5' : 'bg-white/10') : ''}`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                </div>
              );
            })}
            </div>
          </div>
        </div>

        {/* Mega-menu Panel (desktop) */}
        {openMenu && (() => {
          const menu = nav.find((n) => n.name === openMenu);
          const colCount = menu?.columns?.length || 3;
          // Use a static class map so Tailwind keeps the utilities in the build.
          const colClass = {
            2: 'grid-cols-2',
            3: 'grid-cols-3',
            4: 'grid-cols-4',
            5: 'grid-cols-5',
          }[colCount] || 'grid-cols-3';
          return (
          <div
            className="hidden lg:block absolute left-0 right-0 top-full px-4 pointer-events-none"
            data-testid={`mega-menu-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <motion.div
              key={openMenu}
              initial={{ opacity: 0, y: -8, scaleY: 0.985 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              transition={{ duration: dur.short, ease: ease.out }}
              style={{ transformOrigin: 'top center' }}
              className={`bp-megamenu-glass bp-megamenu-glass--tethered ${useDarkText ? 'bp-megamenu-glass--light' : ''} relative max-w-[1280px] mx-auto overflow-hidden pointer-events-auto`}
              onMouseEnter={() => handleEnter(openMenu)}
              onMouseLeave={handleLeave}
            >
              {/* Hover-bridge, extends the hover surface UP over the
                  nav pill so the cursor doesn't lose the menu while
                  crossing diagonally. Pointer-only, zero visual impact. */}
              <span
                aria-hidden="true"
                className="absolute -top-6 left-0 right-0 h-6 pointer-events-auto"
              />
              {menu?.productsMega && (
                <ProductsMegaMenu onNavigate={() => setOpenMenu(null)} />
              )}
              {!menu?.productsMega && (<>
              {/* Optional hero buttons row - only menus that opt in (e.g. Home & Land) */}
              {menu?.heroButtons && menu.heroButtons.length > 0 && (
                <div
                  className="bp-megamenu-glass__hero-band grid grid-cols-1 gap-2.5 p-3 xl:p-3.5"
                  style={{ gridTemplateColumns: `repeat(${menu.heroButtons.length}, minmax(0, 1fr))` }}
                  data-testid={`mega-menu-hero-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {menu.heroButtons.map((b) => {
                    // Hero buttons can either link directly (`href`) OR
                    // open a MiniMatcher with a preset category. The two
                    // modes share identical visuals; only the wrapper
                    // element differs (Link vs button).
                    const hasImg = !!b.image;
                    const wrapperClass = hasImg
                      ? `group relative overflow-hidden rounded-xl min-h-[132px] flex flex-col justify-end p-3 transition-all hover:shadow-[0_18px_40px_rgba(0,0,0,0.22)] hover:-translate-y-0.5 text-left w-full`
                      : `group relative overflow-hidden rounded-xl bg-gradient-to-br ${b.accent} p-3 transition-all hover:shadow-[0_18px_40px_rgba(0,0,0,0.18)] hover:-translate-y-0.5 text-left w-full`;
                    const testId = `mega-menu-hero-btn-${b.title.toLowerCase().replace(/\s+/g, '-')}`;
                    const inner = (
                      <>
                        {hasImg ? (
                          <>
                            <img src={b.image} alt={b.alt || b.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/5" />
                          </>
                        ) : (
                          <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(254,189,89,1) 0%, transparent 55%)' }} />
                        )}
                        <div className="relative flex items-end justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[9.5px] font-semibold uppercase tracking-[0.07em] mb-1" style={{ color: b.eyebrowColor }}>
                              {b.eyebrow}
                            </p>
                            <h4 className="text-[14px] font-bold text-white leading-tight mb-0.5 drop-shadow-sm">
                              {b.title}
                            </h4>
                            <p className="text-[11.5px] text-white/80 leading-[1.35] line-clamp-2">
                              {b.subtitle}
                            </p>
                          </div>
                          <div className="shrink-0 w-7 h-7 rounded-full bg-white/20 group-hover:bg-white/30 backdrop-blur flex items-center justify-center transition-colors">
                            {b.LucideIcon ? (
                              <b.LucideIcon className="w-3.5 h-3.5 text-white" strokeWidth={1.8} />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-white -rotate-90" />
                            )}
                          </div>
                        </div>
                      </>
                    );
                    if (b.matcherCategory || b.matcherCategoryOptions) {
                      return (
                        <button
                          key={`${b.title}-${b.matcherCategory || 'opts'}`}
                          type="button"
                          onClick={() => {
                            setOpenMenu(null);
                            setMatcherState({
                              category: b.matcherCategory || null,
                              categoryOptions: b.matcherCategoryOptions || null,
                              heading: b.matcherHeading || `Find ${b.title.toLowerCase()}`,
                              subheading: b.matcherSubheading || 'One brief. 3 verified Adelaide locals. Zero cold-call spam.',
                              sourcePlacement: `mega-menu-${openMenu}`,
                            });
                          }}
                          className={wrapperClass}
                          data-testid={testId}
                        >
                          {inner}
                        </button>
                      );
                    }
                    return (
                      <Link
                        key={b.href}
                        to={b.href}
                        onClick={() => setOpenMenu(null)}
                        className={wrapperClass}
                        data-testid={testId}
                      >
                        {inner}
                      </Link>
                    );
                  })}
                </div>
              )}

              {menu?.circleStrip && menu.circleStrip.length > 0 && (
                <div
                  className="bp-megamenu-glass__sub px-4 xl:px-5 py-5 xl:py-6"
                  data-testid={`mega-menu-circles-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {/* 2026-02-20: vertical padding bumped from py-3 to py-5
                   * so the hover lift (-translate-y-0.5) + group-hover
                   * shadow don't get clipped by the overflow-x:auto scroll
                   * container (overflow-x:auto implicitly sets overflow-y
                   * to auto, which was visibly cropping circles top/bottom). */}
                  <div
                    className="flex gap-2.5 lg:gap-2 overflow-x-auto -mx-1 px-1 py-2 [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: 'none' }}
                  >
                    {menu.circleStrip.map((t) => {
                      const LucideIcon = t.LucideIcon;
                      return (
                        <Link
                          key={t.href + t.name}
                          to={t.href}
                          onClick={() => setOpenMenu(null)}
                          className="group flex flex-col items-center text-center gap-1.5 flex-shrink-0 w-[88px]"
                          data-testid={`mega-menu-circle-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          <div className={`bp-megamenu-glass__circle w-16 h-16 rounded-full overflow-hidden flex items-center justify-center group-hover:-translate-y-0.5 ${
                            t.iconSrc
                              ? ''
                              : '!bg-[#1d8efe] group-hover:!bg-[#1d8efe] shadow-[0_4px_14px_rgba(29,142,254,0.28)]'
                          }`}>
                            {t.iconSrc ? (
                              <SmartIcon
                                src={`${t.iconSrc}?v=2026022004`}
                                alt=""
                                aria-hidden="true"
                                loading="eager"
                                decoding="async"
                                width="44"
                                height="44"
                                className="w-11 h-11 object-contain"
                              />
                            ) : LucideIcon ? (
                              <LucideIcon className="w-5 h-5 text-white transition-colors" strokeWidth={1.8} />
                            ) : null}
                          </div>
                          <span className="bp-megamenu-glass__circle-label text-[12px] font-medium leading-tight line-clamp-2">
                            {t.name}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {menu?.chipStrip && menu.chipStrip.length > 0 && (
                <div
                  className="bp-megamenu-glass__sub flex flex-nowrap items-center justify-center gap-2 px-4 xl:px-5 py-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  data-testid={`mega-menu-chips-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {menu.chipStrip.map((c) => (
                    <Link
                      key={c.href + c.name}
                      to={c.href}
                      onClick={() => setOpenMenu(null)}
                      className={`bp-megamenu-glass__chip group inline-flex items-center gap-2 rounded-full text-[12.5px] font-semibold whitespace-nowrap shrink-0 transition-transform hover:-translate-y-0.5 ${c.image ? 'pl-1 pr-3.5 py-1' : 'px-2.5 py-1'}`}
                      data-testid={`mega-menu-chip-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {c.image && (
                        <span className="w-7 h-7 rounded-full overflow-hidden shrink-0 ring-1 ring-white/25">
                          <img src={c.image} alt={`${c.name} home style`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                        </span>
                      )}
                      {c.name}
                    </Link>
                  ))}
                </div>
              )}

              {/* HBH "Latest episodes" micro-strip - opt-in per menu via
                  menu.watchStrip. Sits between the hero tiles and the
                  column links. Kept intentionally tiny so it doesn't
                  overpower the secondary menu items below. */}
              {menu?.watchStrip && (
                <div
                  className="bp-megamenu-glass__sub px-4 xl:px-5 py-2.5 border-y border-white/[0.06]"
                  data-testid={`mega-menu-watch-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <p className="text-[9.5px] font-semibold uppercase tracking-[0.07em] text-white/50">
                        {menu.watchStrip.eyebrow || 'Latest episodes'}
                      </p>
                      <HbhAttribution variant="line" dark />
                    </div>
                    {menu.watchStrip.ctaHref && (
                      <Link
                        to={menu.watchStrip.ctaHref}
                        onClick={() => setOpenMenu(null)}
                        className="text-[10.5px] font-semibold text-white/70 hover:text-white transition-colors"
                        data-testid="mega-menu-watch-cta"
                      >
                        {menu.watchStrip.ctaLabel || 'Watch all'} →
                      </Link>
                    )}
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {getRecentHbhEpisodes(menu.watchStrip.count || 6).map((e) => (
                      <Link
                        key={e.slug}
                        to={`/watch/${e.slug}`}
                        onClick={() => setOpenMenu(null)}
                        className="group flex flex-col rounded-lg bg-white/[0.04] hover:bg-white/[0.08] overflow-hidden transition-colors"
                        data-testid={`mega-menu-watch-item-${e.slug}`}
                      >
                        <div className="relative aspect-video bg-black">
                          <img
                            src={e.thumbnail}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition-colors">
                            <div className="w-5 h-5 rounded-full bg-white/95 flex items-center justify-center">
                              <div className="w-0 h-0 border-t-[4px] border-b-[4px] border-l-[6px] border-t-transparent border-b-transparent border-l-[#1d1d1f] ml-0.5" />
                            </div>
                          </div>
                          {e.duration && (
                            <span className="absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 py-0.5 text-[8.5px] font-medium text-white">
                              {e.duration}
                            </span>
                          )}
                        </div>
                        <div className="p-1.5">
                          <p className="text-[10.5px] font-semibold text-white leading-tight line-clamp-2 group-hover:text-[#4abff2] transition-colors">
                            {e.title}
                          </p>
                          <p className="text-[9px] text-white/40 mt-0.5">Ep {e.number}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className={`grid ${colClass} gap-5 xl:gap-6 px-5 xl:px-6 py-4 xl:py-5`}>
                {menu?.columns?.map((col) => (
                  <div key={col.title}>
                    <h3 className="bp-megamenu-glass__eyebrow text-[11px] font-semibold uppercase tracking-[0.06em] mb-2.5">
                      {col.title}
                    </h3>
                    <ul className="space-y-1.5">
                      {col.links.map((link) => (
                        <li key={`${link.href}::${link.name}`}>
                          <Link
                            to={link.href}
                            onClick={() => setOpenMenu(null)}
                            className="group block"
                          >
                            <span className="bp-megamenu-glass__link-title text-[14px] font-medium leading-[1.35] block">
                              {link.name}
                            </span>
                            {link.description && (
                              <span className="bp-megamenu-glass__link-desc text-[12px] leading-[1.4] block mt-0.5">
                                {link.description}
                              </span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Featured local estate tiles (Explore Home & Land only).
                  State-aware: SA default, Melbourne set for VIC visitors. */}
              {menu?.estateTiles?.length > 0 && (
                <div
                  className="px-5 xl:px-6 pb-4 xl:pb-5"
                  data-testid={`mega-menu-estate-tiles-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <h3 className="bp-megamenu-glass__eyebrow text-[11px] font-semibold uppercase tracking-[0.06em]">
                      Explore local estates
                    </h3>
                    {menu.estateTilesViewAll && (
                      <Link
                        to={menu.estateTilesViewAll.href}
                        onClick={() => setOpenMenu(null)}
                        className="bp-megamenu-glass__footer-link text-[12px] font-medium"
                        data-testid="mega-menu-estate-tiles-view-all"
                      >
                        {menu.estateTilesViewAll.label} →
                      </Link>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
                    {menu.estateTiles.map((t) => (
                      <Link
                        key={t.href}
                        to={t.href}
                        onClick={() => setOpenMenu(null)}
                        className={`group relative overflow-hidden rounded-xl min-h-[94px] flex flex-col justify-end bg-gradient-to-br ${t.accent} hover:-translate-y-0.5 transition-transform`}
                        data-testid={`mega-menu-estate-tile-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {t.img && (
                          <img
                            src={t.img}
                            alt={`${t.name}, ${t.locality}`}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                        <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5 group-hover:from-black/75 transition-colors" />
                        {t.logo && (
                          <span className="absolute top-2 left-2 w-7 h-7 rounded-md bg-white/95 shadow-sm flex items-center justify-center overflow-hidden" data-testid={`mega-menu-estate-logo-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                            <img
                              src={t.logo}
                              alt={`${t.name} logo`}
                              loading="lazy"
                              className="w-5 h-5 object-contain"
                              onError={(e) => { const p = e.currentTarget.parentElement; if (p) p.style.display = 'none'; }}
                            />
                          </span>
                        )}
                        <span className="relative px-3 pb-2.5 pt-7">
                          <span className="block text-[13px] font-semibold text-white leading-tight drop-shadow-sm">{t.name}</span>
                          <span className="flex items-center gap-1 mt-0.5 text-[10.5px] text-white/85 drop-shadow-sm">
                            <Map className="w-3 h-3" /> {t.locality}
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="bp-megamenu-glass__footer px-5 xl:px-6 py-2.5 flex items-center justify-between gap-4">
                {/* Left: subtle "view all" navigation link */}
                <Link
                  to={menu?.href || '/'}
                  onClick={() => setOpenMenu(null)}
                  className="bp-megamenu-glass__footer-link text-[13px] font-medium"
                >
                  View all {openMenu.toLowerCase()} →
                </Link>
                {/* Right cluster: primary gradient-blue matcher CTA
                    (when the menu has a bottomMatcher), with the
                    "Need a human?" link demoted to a smaller secondary
                    link to its left so the matcher CTA can be the
                    visual anchor. */}
                <div className="flex items-center gap-4">
                  <Link
                    to="/one-on-one-support"
                    onClick={() => setOpenMenu(null)}
                    className="bp-megamenu-glass__footer-link bp-megamenu-glass__footer-link--muted text-[12px] font-medium hidden sm:inline"
                  >
                    Need a human?
                  </Link>
                  {menu?.bottomMatcher ? (
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenu(null);
                        setMatcherState({
                          category: null,
                          heading: menu.bottomMatcher.heading,
                          subheading: menu.bottomMatcher.subheading,
                          categoryOptions: menu.bottomMatcher.categoryOptions,
                          sourcePlacement: `mega-menu-${openMenu.toLowerCase().replace(/\s+/g, '-')}-bottom`,
                        });
                      }}
                      className="bp-mm-intel-cta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold hover:-translate-y-0.5 transition-transform"
                      data-testid={`mega-menu-bottom-matcher-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {menu.bottomMatcher.label}
                      <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                    </button>
                  ) : menu?.bottomLink ? (
                    /* bottomLink is a simple navigation CTA styled
                       identically to bottomMatcher - used by menus
                       where the "shortlist" action is browsing a live
                       inventory page, not opening the matcher. */
                    <Link
                      to={menu.bottomLink.href}
                      onClick={() => setOpenMenu(null)}
                      className="bp-mm-intel-cta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold hover:-translate-y-0.5 transition-transform"
                      data-testid={`mega-menu-bottom-link-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {menu.bottomLink.label}
                      <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                    </Link>
                  ) : menu?.bottomHnlMatcher ? (
                    /* bottomHnlMatcher launches the streamlined H&L
                       package matcher popup (separate component from
                       the business MiniMatcher; queries /api/packages). */
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenu(null);
                        setHnlMatcherOpen(true);
                      }}
                      className="bp-mm-intel-cta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold hover:-translate-y-0.5 transition-transform"
                      data-testid={`mega-menu-bottom-hnl-matcher-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {menu.bottomHnlMatcher.label}
                      <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                    </button>
                  ) : menu?.bottomHomeDesignsMatcher ? (
                    /* bottomHomeDesignsMatcher launches the streamlined
                       floor-plan matcher popup, scoped to the home
                       designs library (/api/home-designs/plans). */
                    <button
                      type="button"
                      onClick={() => {
                        setOpenMenu(null);
                        setHomeDesignsMatcherOpen(true);
                      }}
                      className="bp-mm-intel-cta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold hover:-translate-y-0.5 transition-transform"
                      data-testid={`mega-menu-bottom-home-designs-matcher-${openMenu.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {menu.bottomHomeDesignsMatcher.label}
                      <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                    </button>
                  ) : (
                    <Link
                      to="/one-on-one-support"
                      onClick={() => setOpenMenu(null)}
                      className="bp-megamenu-glass__footer-link text-[13px] font-medium sm:hidden"
                    >
                      Talk to a real human →
                    </Link>
                  )}
                </div>
              </div>
              </>)}
            </motion.div>
          </div>
          );
        })()}

        {/* Mobile Menu - Full Screen Overlay
            z-[70] sits ABOVE the sticky header (z-[60]) and the
            announcement bar so the menu's black background fully
            covers the page. The inner padding mirrors the original
            header nav (`pt-4 sm:pt-5 lg:pt-6`) so the logo + close
            land at the same y-position as the closed-state header,
            with zero visual jump on open/close. */}
        {mobileMenuOpen && createPortal(
          <div
            className="fixed inset-0 bg-black/95 backdrop-blur-md lg:hidden z-[70] flex flex-col"
          >
            <div className="px-4 sm:px-6 lg:px-8 pt-[26px] sm:pt-[30px] lg:pt-[34px]">
              <div className="flex items-center justify-between">
                <Link to="/" className="flex items-center" onClick={() => setMobileMenuOpen(false)}>
                  <img
                    src="/images/buildpilot-logo-2026.png"
                    alt="BuildPilot"
                    className="h-[28px] sm:h-[32px] w-auto" loading="lazy" decoding="async" />
                </Link>
                <button
                  className="w-11 h-11 rounded-full border border-white/40 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                  aria-label="Close menu"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            <nav
              className="flex-1 overflow-y-auto px-6 py-6 bp-mobile-menu-scroll"
            >
              {/* Primary CTAs surfaced at the top */}
              <div className="grid grid-cols-2 gap-2.5 mb-5">
                <Link
                  to="/one-on-one-support"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center py-3 rounded-full text-[14px] font-medium text-white border border-white/30 hover:bg-white/10 transition-colors"
                  data-testid="mobile-cta-one-on-one"
                >
                  Talk to a real human
                </Link>
                {auth.isLoggedIn ? (
                  <button
                    onClick={goToDashboard}
                    className="flex items-center justify-center gap-2 py-3 rounded-full text-[14px] font-medium text-white gradient-orange-bg hover:opacity-90 transition-opacity"
                    data-testid="mobile-cta-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4" aria-hidden="true" />
                    My dashboard
                  </button>
                ) : (
                  <button
                    onPointerDown={captureAuthReturnY}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openLoginModal}
                    className="flex items-center justify-center py-3 rounded-full text-[14px] font-medium text-white gradient-orange-bg hover:opacity-90 transition-opacity"
                    data-testid="mobile-cta-sign-in"
                  >
                    Sign in / Sign up
                  </button>
                )}
              </div>

              {/* State context — compact pill directly under the primary
                  CTAs so navigation can lead. Expands to the state list on
                  tap. The heavier utility links (Partner / Talk to a human /
                  Google source) are demoted to a lighter group below the nav. */}
              <div className="mb-5">
                <StateContextChip inMenu compactInMenu />
              </div>

              <ul className="space-y-2">
                {nav.map((item) => {
                  const isOpen = openMobileSection === item.name;
                  if (item.productsMega) {
                    return (
                      <li key={item.name} className="border-b border-white/10">
                        <button
                          type="button"
                          onClick={() => setOpenMobileSection(isOpen ? null : item.name)}
                          className="w-full py-3 flex items-center justify-between text-white text-[18px] font-medium"
                          data-testid="mobile-nav-products"
                        >
                          <span>{item.name}</span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="pb-5 pt-1 space-y-1.5">
                            <Link
                              to="/products"
                              onClick={() => setMobileMenuOpen(false)}
                              className="block py-2 text-[#8cc6ff] text-[14px] font-semibold"
                              data-testid="mobile-products-shopall"
                            >
                              Shop all products →
                            </Link>
                            {PRODUCTS_DEPARTMENTS.map((d) => {
                              const dOpen = openMobileDept === d.key;
                              return (
                                <div key={d.key} className="border-t border-white/5">
                                  <button
                                    type="button"
                                    onClick={() => setOpenMobileDept(dOpen ? null : d.key)}
                                    className="w-full py-2.5 flex items-center justify-between text-white/90 text-[15px] font-medium"
                                    data-testid={`mobile-products-dept-${d.key}`}
                                  >
                                    <span>{d.label}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${dOpen ? 'rotate-180' : ''}`} />
                                  </button>
                                  {dOpen && (
                                    <div className="pb-3 pl-3 flex flex-col gap-0.5">
                                      <Link
                                        to={`/products/${d.key}`}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="py-1.5 text-[#8cc6ff] text-[13.5px] font-semibold"
                                        data-testid={`mobile-products-viewall-${d.key}`}
                                      >
                                        All {d.label}
                                      </Link>
                                      {d.subcategories.map((sc) => (
                                        <Link
                                          key={sc.cat + sc.label}
                                          to={`/products/${d.key}?cat=${sc.cat}`}
                                          onClick={() => setMobileMenuOpen(false)}
                                          className="py-1.5 text-white/75 text-[13.5px]"
                                          data-testid={`mobile-products-subcat-${sc.cat}`}
                                        >
                                          {sc.label}
                                        </Link>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <div className="border-t border-white/5 pt-3">
                              <span className="block text-white/50 text-[11px] font-semibold uppercase tracking-[0.04em] mb-2">Shop by brand</span>
                              <div className="flex flex-wrap gap-2">
                                {PRODUCTS_BRANDS.map((b) => (
                                  <Link
                                    key={b.key}
                                    to={b.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="px-3 py-1.5 rounded-full border border-white/20 text-white/85 text-[13px]"
                                    data-testid={`mobile-products-brand-${b.key}`}
                                  >
                                    {b.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  }
                  if (!item.columns) {
                    // Skip One on One Support since it's surfaced as a primary CTA above
                    if (item.name === "Talk to a real human") return null;
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block py-3 text-white text-[18px] font-medium border-b border-white/10"
                          data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.name}
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={item.name} className="border-b border-white/10">
                      <button
                        type="button"
                        onClick={() => setOpenMobileSection(isOpen ? null : item.name)}
                        className="w-full py-3 flex items-center justify-between text-white text-[18px] font-medium"
                        data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="pb-5 pt-1 space-y-4">
                          {/* Primary matcher / shortlist CTA - mirrors desktop bottom-of-mega-menu */}
                          {item.bottomMatcher ? (
                            <button
                              type="button"
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setMatcherState({
                                  category: null,
                                  heading: item.bottomMatcher.heading,
                                  subheading: item.bottomMatcher.subheading,
                                  categoryOptions: item.bottomMatcher.categoryOptions,
                                  sourcePlacement: `mobile-menu-${item.name.toLowerCase().replace(/\s+/g, '-')}-matcher`,
                                });
                              }}
                              className="w-full bp-mm-intel-cta inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-[14px] font-semibold"
                              data-testid={`mobile-menu-matcher-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.bottomMatcher.label}
                              <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                          ) : item.bottomHnlMatcher ? (
                            <button
                              type="button"
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setHnlMatcherOpen(true);
                              }}
                              className="w-full bp-mm-intel-cta inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-[14px] font-semibold"
                              data-testid={`mobile-menu-hnl-matcher-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.bottomHnlMatcher.label}
                              <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                          ) : item.bottomHomeDesignsMatcher ? (
                            <button
                              type="button"
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setHomeDesignsMatcherOpen(true);
                              }}
                              className="w-full bp-mm-intel-cta inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-[14px] font-semibold"
                              data-testid={`mobile-menu-home-designs-matcher-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.bottomHomeDesignsMatcher.label}
                              <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                          ) : item.bottomLink ? (
                            <Link
                              to={item.bottomLink.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="w-full bp-mm-intel-cta inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-[14px] font-semibold"
                              data-testid={`mobile-menu-bottom-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.bottomLink.label}
                              <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                            </Link>
                          ) : null}

                          {/* Hero cards (stacked) */}
                          {item.heroButtons && item.heroButtons.length > 0 && (
                            <div className="space-y-2">
                              {item.heroButtons.map((b) => (
                                <Link
                                  key={b.href + b.title}
                                  to={b.href}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className={b.image
                                    ? `relative block overflow-hidden rounded-xl min-h-[96px] flex flex-col justify-end p-3.5`
                                    : `relative block overflow-hidden rounded-xl bg-gradient-to-br ${b.accent} p-3.5`}
                                  data-testid={`mobile-hero-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {b.image ? (
                                    <>
                                      <img src={b.image} alt={b.alt || b.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/5" />
                                    </>
                                  ) : (
                                    <div className="absolute inset-0 opacity-[0.1] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(254,189,89,1) 0%, transparent 55%)' }} />
                                  )}
                                  <div className="relative flex items-end justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-[9px] font-semibold uppercase tracking-[0.07em] mb-0.5" style={{ color: b.eyebrowColor }}>
                                        {b.eyebrow}
                                      </p>
                                      <h4 className="text-[14px] font-bold text-white leading-tight mb-0.5">
                                        {b.title}
                                      </h4>
                                      <p className="text-[11.5px] text-white/80 leading-[1.35] line-clamp-2">
                                        {b.subtitle}
                                      </p>
                                    </div>
                                    <div className="shrink-0 w-7 h-7 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                                      <ChevronDown className="w-3.5 h-3.5 text-white -rotate-90" />
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          )}

                          {/* Circle strip - 4-column grid */}
                          {item.circleStrip && item.circleStrip.length > 0 && (
                            <div className="grid grid-cols-4 gap-2 bg-white/5 rounded-xl p-3">
                              {item.circleStrip.map((t) => {
                                const LucideIcon = t.LucideIcon;
                                return (
                                  <Link
                                    key={t.href + t.name}
                                    to={t.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex flex-col items-center text-center gap-1.5"
                                    data-testid={`mobile-circle-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                  >
                                    <div className={`w-16 h-16 rounded-full overflow-hidden flex items-center justify-center ${
                                      t.iconSrc ? 'bg-white' : 'bg-[#1d8efe] shadow-[0_4px_12px_rgba(29,142,254,0.32)]'
                                    }`}>
                                      {t.iconSrc ? (
                                        <SmartIcon src={t.iconSrc} alt="" aria-hidden="true" loading="eager" decoding="async" width="48" height="48" className="w-12 h-12 object-contain" />
                                      ) : LucideIcon ? (
                                        <LucideIcon className="w-5 h-5 text-white" strokeWidth={1.8} />
                                      ) : null}
                                    </div>
                                    <span className="text-[10px] font-medium leading-tight text-white/85 line-clamp-2">
                                      {t.name}
                                    </span>
                                  </Link>
                                );
                              })}
                            </div>
                          )}

                          {/* Chip strip - horizontal scroll */}
                          {item.chipStrip && item.chipStrip.length > 0 && (
                            <div className="-mx-6 px-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                              <div className="flex items-center gap-2 w-max">
                                {item.chipStrip.map((c) => (
                                  <Link
                                    key={c.href + c.name}
                                    to={c.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`inline-flex items-center gap-2 rounded-full text-[12.5px] font-semibold whitespace-nowrap ${c.image ? 'pl-1 pr-3 py-1 bg-white/10 border border-white/20 text-white' : 'px-3 py-1.5 border border-white/20 bg-white/5 text-white'}`}
                                    data-testid={`mobile-chip-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                  >
                                    {c.image && (
                                      <span className="w-6 h-6 rounded-full overflow-hidden shrink-0 ring-1 ring-white/20">
                                        <img src={c.image} alt={`${c.name} home style`} loading="lazy" className="w-full h-full object-cover" />
                                      </span>
                                    )}
                                    {c.name}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* HBH watch strip - horizontal-scroll video cards.
                              Mirrors the desktop watchStrip but uses a swipe
                              rail since 6 tiles won't fit at mobile widths. */}
                          {item.watchStrip && (
                            <div>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <p className="text-[9.5px] font-semibold uppercase tracking-[0.07em] text-white/50">
                                    {item.watchStrip.eyebrow || 'Latest episodes'}
                                  </p>
                                  <HbhAttribution variant="line" dark />
                                </div>
                                {item.watchStrip.ctaHref && (
                                  <Link
                                    to={item.watchStrip.ctaHref}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="text-[10.5px] font-semibold text-white/75 hover:text-white"
                                    data-testid="mobile-menu-watch-cta"
                                  >
                                    {item.watchStrip.ctaLabel || 'Watch all'} →
                                  </Link>
                                )}
                              </div>
                              <div className="-mx-6 px-6 overflow-x-auto snap-x snap-mandatory" style={{ scrollbarWidth: 'none' }}>
                                <div className="flex gap-2.5 w-max">
                                  {getRecentHbhEpisodes(item.watchStrip.count || 6).map((e) => (
                                    <Link
                                      key={e.slug}
                                      to={`/watch/${e.slug}`}
                                      onClick={() => setMobileMenuOpen(false)}
                                      className="group flex flex-col rounded-lg bg-white/[0.05] hover:bg-white/[0.09] overflow-hidden transition-colors w-[150px] shrink-0 snap-start"
                                      data-testid={`mobile-watch-item-${e.slug}`}
                                    >
                                      <div className="relative aspect-video bg-black">
                                        <img
                                          src={e.thumbnail}
                                          alt=""
                                          aria-hidden="true"
                                          className="absolute inset-0 w-full h-full object-cover"
                                          loading="lazy"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                                          <div className="w-6 h-6 rounded-full bg-white/95 flex items-center justify-center">
                                            <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[7px] border-t-transparent border-b-transparent border-l-[#1d1d1f] ml-0.5" />
                                          </div>
                                        </div>
                                        {e.duration && (
                                          <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 py-0.5 text-[9px] font-medium text-white">
                                            {e.duration}
                                          </span>
                                        )}
                                      </div>
                                      <div className="p-2">
                                        <p className="text-[11.5px] font-semibold text-white leading-tight line-clamp-2">
                                          {e.title}
                                        </p>
                                        <p className="text-[9.5px] text-white/45 mt-0.5">Ep {e.number}</p>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Simplified link list, flatten only the first (primary) column to keep it compact */}
                          {item.columns[0] && (
                            <div className="pt-1">
                              <p className="text-[10px] uppercase tracking-[0.06em] text-white/45 mb-2">{item.columns[0].title}</p>
                              <ul className="space-y-1">
                                {item.columns[0].links.map((link) => (
                                  <li key={link.href + link.name}>
                                    <Link
                                      to={link.href}
                                      onClick={() => setMobileMenuOpen(false)}
                                      className="block py-1.5 text-[14px] text-white/85"
                                    >
                                      {link.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <Link
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="inline-flex items-center gap-1.5 py-2 text-[13px] text-[#7fb3e8] font-medium"
                            data-testid={`mobile-view-all-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            View all {item.name.toLowerCase()} →
                          </Link>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Demoted utility group — secondary actions moved below the
                  primary navigation so they stop crowding the top of the menu. */}
              <div className="mt-7 pt-5 border-t border-white/10 space-y-1">
                <Link
                  to="/register-business"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between py-2.5 text-white/75 text-[14px] hover:text-white transition-colors"
                  data-testid="mobile-cta-register-business"
                >
                  Become a Partner
                  <ChevronDown className="w-4 h-4 -rotate-90 text-white/40" />
                </Link>
                <button
                  onClick={handleGetInTouch}
                  className="w-full flex items-center justify-between py-2.5 text-white/75 text-[14px] hover:text-white transition-colors text-left"
                  data-testid="mobile-cta-get-in-touch-link"
                >
                  Talk to a human → Get in touch
                  <ChevronDown className="w-4 h-4 -rotate-90 text-white/40" />
                </button>
                <div className="pt-2">
                  <PreferredSourceButton variant="menu" />
                </div>
              </div>
            </nav>

            <div className="px-6 pb-12 pt-4 border-t border-white/10">
              <a
                href="https://buildpilot.app"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center py-3.5 rounded-full text-base font-semibold text-white gradient-text-purple-bg transition-all hover:opacity-90"
                style={{ textShadow: '0 1px 3px rgba(0,0,0,0.55), 0 0 18px rgba(0,0,0,0.25)' }}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-download-app-btn"
              >
                Download App
              </a>
            </div>
          </div>,
          document.body,
        )}
      </nav>
      <EmailForwardingModal
        open={emailFwdOpen}
        userId={auth.user?.id}
        onClose={() => setEmailFwdOpen(false)}
      />
      {/* MiniMatcher launched from mega-menu hero buttons. Keyed by
          category so reopening with a different category mounts a
          fresh form (no stale brief state from the previous category).
          2026-02-19: initialState is only passed for logged-in users
          with a known profile.state. We never default to SA - logged-out
          users must explicitly choose their state on the account step. */}
      <MiniMatcher
        key={(matcherState?.category || matcherState?.heading || 'closed')}
        open={!!matcherState}
        onClose={() => setMatcherState(null)}
        category={matcherState?.category}
        categoryOptions={matcherState?.categoryOptions}
        sourcePlacement={matcherState?.sourcePlacement || 'mega-menu-hero'}
        sourcePage={location.pathname}
        initialState={matcherState?.initialState || (auth.isLoggedIn ? (auth.profile?.state || null) : null)}
        heading={matcherState?.heading}
        subheading={matcherState?.subheading}
      />
      <MiniHnlMatcher
        open={hnlMatcherOpen}
        onClose={() => setHnlMatcherOpen(false)}
        sourcePlacement="mega-menu-hnl-bottom"
        sourcePage={location.pathname}
        initialState={auth.isLoggedIn ? (auth.profile?.state || null) : null}
      />
      <MiniHomeDesignsMatcher
        open={homeDesignsMatcherOpen}
        onClose={() => setHomeDesignsMatcherOpen(false)}
        sourcePlacement="mega-menu-home-designs-bottom"
        sourcePage={location.pathname}
        initialState={auth.isLoggedIn ? (auth.profile?.state || null) : null}
      />
    </header>
    </>
  );
};

export default Header;
